import type { OsintLookup } from "./osint";

// === COMMON RESPONSE TYPES (API Contract) ===

/**
 * Метаданные запроса CommonResponse.
 */
export interface CommonResponseMetadata {
  request_id: string;
  timestamp: string;
  requested_at: string;
  completed_at: string | null;
  processing_time_ms: number;
  data_freshness: string;
  api_version: string;
}

/**
 * Метаданные API модуля CommonResponse.
 */
export interface CommonResponseApiMetadata {
  module: string;
  version: string;
  parser_version: string;
  connection_type: string;
  retry_count: number;
}

/**
 * Метаданные OSINT обогащения в ответе валидации
 */
export interface OsintMetadata {
  task_id: string;
  status: string;
  batch_id?: string;
  message?: string;
}

/**
 * Базовый интерфейс CommonResponse.
 * Все API модули возвращают результаты в этом формате.
 */
export interface CommonResponse<T = unknown> {
  status: "COMPLETED" | "ERROR";
  success: boolean;
  module_name: string;
  data: T;
  data_actual_date: string;
  metadata: CommonResponseMetadata;
  api_metadata: CommonResponseApiMetadata;
  error: string | null;
  /** OSINT обогащение (опционально, для phone/email валидации) */
  osint?: OsintMetadata;
}

// === PRE-VALIDATED DATA TYPES (for Complex360 Report) ===

/**
 * Данные ФИО, уже провалидированные на фронтенде.
 */
export interface NameDataInput {
  surname: string;
  name: string;
  patronymic?: string;
}

/**
 * Паспорт, провалидированный на фронтенде.
 */
export interface ValidatedPassportInput {
  series: string;
  number: string;
  issue_date?: string;
  is_valid?: boolean;
}

/**
 * Водительское удостоверение, провалидированное на фронтенде.
 */
export interface ValidatedDriverLicenseInput {
  normalized: string;
  issue_date?: string;
  is_valid?: boolean;
}

/**
 * Pre-validated данные персоны от фронтенда.
 * Используется для пропуска ValidationPhase на бекенде.
 */
export interface ValidatedPersonDataInput {
  full_name: string;
  name_data: NameDataInput;
  birth_date: string;
  birth_place?: string;
  tax_id?: string;
  snils?: string;
  passports?: ValidatedPassportInput[];
  driver_licenses?: ValidatedDriverLicenseInput[];
  addresses?: string[];
  phones?: string[];
  emails?: string[];
}

// === PERSON DATA TYPES ===

export interface PersonData {
  last_name?: string;
  first_name?: string;
  middle_name?: string;
  birth_date?: string;
  /** @deprecated Use tax_id instead */
  inn?: string;
  /** ИНН (10 или 12 цифр) */
  tax_id?: string;
  ssn?: string;
  passport_series?: string;
  passport_number?: string;
  passport_issue_date?: string;
  drivers_license_series?: string;
  drivers_license_number?: string;
  drivers_license_issue_date?: string;
  birth_place?: string;
  registration_address?: string;
  phone_number?: string;
  email?: string;
  /** Список телефонов для валидации */
  phones?: string[];
  /** Список email для валидации */
  emails?: string[];
  /** Список адресов для валидации */
  addresses?: string[];
  /** Тип документа об образовании: vpo, spo, school, po, dpo, migrant */
  edu_diploma_type?: string;
  /** Подтип ДПО: certificate (повышение квалификации) или diploma (переподготовка) */
  edu_dpo_subtype?: string;
  /** Фамилия в документе об образовании */
  edu_surname?: string;
  /** Серия бланка документа об образовании */
  edu_series?: string;
  /** Номер бланка документа об образовании */
  edu_number_blank?: string;
  /** Дата выдачи документа об образовании (ДД.ММ.ГГГГ) */
  edu_issue_date?: string;
  /** Название образовательной организации */
  edu_org_name?: string;
  /** ОГРН образовательной организации */
  edu_org_ogrn?: string;
  /** Фамилия на латинском (для сертификата мигранта) */
  edu_surname_lat?: string;
  /** Регистрационный номер документа об образовании */
  edu_register_number?: string;

  // === COMPANY DATA ===
  /** Наименование организации */
  company_name?: string;
  /** ИНН организации (10 цифр) */
  company_inn?: string;
  /** ОГРН организации (13 цифр) */
  company_ogrn?: string;

  // === VEHICLE DATA ===
  /** Государственный регистрационный номер ТС */
  vehicle_plate?: string;
  /** VIN номер ТС (17 символов) */
  vehicle_vin?: string;
  /** Номер СТС */
  vehicle_sts?: string;
}

/** Информация о доступности модуля */
export interface ModuleAvailability {
  module_name: string;
  display_name: string;
  description?: string;
  category: string;
  priority?: number;
  is_available: boolean;
  confidence?: number;
  missing_fields?: string[];
  reasoning?: string;
  api_endpoint?: string;
  estimated_duration_ms?: number;
  is_async?: boolean;
}

export interface DataAnalysis {
  has_full_name: boolean;
  has_birth_date: boolean;
  has_inn: boolean;
  has_passport?: boolean;
  has_legal_data?: boolean;
  data_completeness: number;
  available_data_combinations?: string[];
  recommended_parsers: string[];
  analysis_reasoning?: string[];
  improvement_suggestions: string[];
  /** Доступные модули с детальной информацией */
  available_modules?: ModuleAvailability[];
  /** Недоступные модули с указанием причины */
  unavailable_modules?: ModuleAvailability[];
}

export interface ParserExecutionPlan {
  parser_type: string;
  priority: number;
  expected_duration: number;
  reasoning: string;
  required_fields: string[];
}

export interface CheckSummary {
  total_matches: number;
  high_risk_matches: number;
  parsers_executed: number;
  parsers_successful: number;
  parsers_failed: number;
  risk_level: "low" | "medium" | "high";
  recommendations: string[];
}

export interface ExecutionStats {
  total_execution_time: number;
  fastest_parser?: string;
  slowest_parser?: string;
  average_response_time: number;
  parallel_efficiency: number;
}

export interface UniversalParserResult {
  found?: boolean;
  data?: {
    risk_flags?: RiskFlag[];
  } | null;
  error?: string;
}

export interface UniversalCheckResult {
  request_id: string;
  person_data: PersonData;
  data_analysis: DataAnalysis;
  execution_plan: ParserExecutionPlan[];
  parser_results: Record<string, UniversalParserResult | null>;
  summary: CheckSummary;
  execution_stats: ExecutionStats;
  timestamp: string;
}

export interface UniversalCheckConfig {
  priority_mode?: boolean;
  parallel_execution?: boolean;
  timeout?: number;
  include_parsers?: string[];
  exclude_parsers?: string[];
}

export interface UniversalCheckRequest {
  person_data: PersonData;
  check_config?: UniversalCheckConfig;
}

// Асинхронные проверки
export interface AsyncCheckRequest extends UniversalCheckRequest {
  callback_url?: string;
  priority?: number;
}

export interface AsyncCheckResponse {
  task_id: string;
  estimated_completion: string;
  status: string;
}

/**
 * Статусы задач по контракту API v2.3.
 * Только UPPERCASE!
 */
export type TaskStatusType = "PENDING" | "STARTED" | "PROGRESS" | "COMPLETED" | "FAILED";

export interface AsyncTaskStatus {
  task_id: string;
  status: TaskStatusType | string; // string для совместимости с различными форматами бэкенда
  error?: string;
  result?: unknown;
  progress?: number;
  current_parser?: string;
  completed_parsers?: string[];
  failed_parsers?: string[];
  estimated_remaining?: number;
  created_at?: string;
  // Новые поля для детального прогресса
  current_step?: string;
  message?: string;
  current?: number;
  total?: number;
}

// Live-проверки
export interface LiveCheckRequest extends UniversalCheckRequest {
  session_name?: string;
}

export interface ParserTaskInfo {
  parser_type: string;
  task_id: string;
  status: string;
  priority: number;
  estimated_time: number;
  started_at?: string;
  result?: Record<string, unknown>;
  error?: string;
}

export interface LiveReportStructure {
  session_id: string;
  person_data: PersonData;
  data_analysis: DataAnalysis;
  parsers: Record<string, ParserTaskInfo>;
  overall_status: string;
  progress: number;
  started_at: string;
  preliminary_summary?: CheckSummary;
}

// Ответы API
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  timestamp: string;
  task_id?: string;
}

export interface ApiError {
  success: false;
  error: string;
  message: string;
  details?: Record<string, unknown>;
  timestamp: string;
}

// Возможности парсеров
export interface ParserCapabilities {
  parser_type: string;
  required_fields: string[];
  optional_fields: string[];
  estimated_time: number;
  success_rate: number;
  data_sources: string[];
}

export interface ContextModuleInfo {
  version: string;
  total_modules: number;
  categories: Record<string, string[]>;
  modules: Array<{
    type: string;
    name: string;
    category: string;
    priority: number;
    required_fields: string[];
    optional_fields: string[];
    estimated_time: number;
  }>;
  features: string[];
}

// Результаты отдельных парсеров
export interface ParserResult {
  found: boolean;
  validation_date: string;
  data?: unknown;
  error?: string;
  execution_time?: number;
}

// Конкретные типы результатов парсеров
export interface FSSPDebtorResult extends ParserResult {
  full_name: string;
  birth_date: string;
  records_found: number;
  data?: Array<{
    "Розыскное дело (№, дата) Исполнительное производство (№, дата)": string;
    "Объект розыска": string;
    "Данные об объявлении розыска должника, его имущества или ребенка": string;
    "Наименование ОСП, где осуществляется розыск, адрес": string;
    "Телефон дежурной части ТО ФССП России": string;
  }>;
}

export interface FSSPIPDebtorResult extends ParserResult {
  full_name: string;
  birth_date: string;
  data?: {
    total_debts: number;
    open_debts: number;
    closed_debts: number;
    total_debt: number;
    open_debt: number;
    closed_debt: number;
    open: Array<{
      debtor_info: string;
      case_number: string;
      enforcement_document: string;
      completion_reason: string;
      debt_details: string;
      bailiff_department: string;
      bailiff_executor: string;
      debt_amount: number;
      case_status: string;
    }>;
    closed: Array<{
      debtor_info: string;
      case_number: string;
      enforcement_document: string;
      completion_reason: string;
      debt_details: string;
      bailiff_department: string;
      bailiff_executor: string;
      debt_amount: number;
      case_status: string;
    }>;
  };
}

/**
 * Запись исполнительного производства ФССП (API Contract 2.4).
 * Используется как элемент массива response.data.results.
 */
export interface FsspIpDebtorRecord {
  status?: "open" | "closed";
  debtor_name?: string;
  birth_date?: string;
  birth_place?: string;
  case_number?: string;
  debt_amount?: string | number;
  debt_amount_formatted?: string;
  document_number?: string;
  court_name?: string;
  bailiff_name?: string;
  bailiff_phone?: string;
  bailiff_office?: string;
  bailiff_address?: string;
  closure_date?: string;
  closure_reason?: string;
  creditor_inn?: string;
}

/**
 * CommonResponse для ФССП: исполнительные производства (API Contract 2.4).
 */
export type FsspIpDebtorCommonResponse = ComplianceCommonResponse<FsspIpDebtorRecord>;

export interface FNSTaxDebtorResult extends ParserResult {
  tax_id: string;
  data?: Array<{
    accrual_type: string;
    accrual_basis: string;
    tax_debt: string;
  }>;
}

/**
 * Запись алиментного дела ФССП.
 * Соответствует backend FsspAlimonyCase.to_dict() (API Contract 2.3)
 */
export interface FsspAlimonyCaseRecord {
  /** Номер исполнительного производства */
  case_number: string;
  /** Дата возбуждения ИП */
  case_start_date?: string;
  /** ФИО должника */
  debtor_name: string;
  /** Дата рождения */
  birth_date?: string;
  /** Место рождения */
  birth_place?: string;
  /** Тип исполнительного документа */
  executive_document_type?: string;
  /** Дата исполнительного документа */
  executive_document_date?: string;
  /** Номер исполнительного документа */
  executive_document_number?: string;
  /** Полная строка документа (для совместимости) */
  document_number?: string;
  /** Взыскатель (получатель алиментов) */
  creditor_name?: string;
  /** Наименование суда */
  court_name?: string;
  /** Сумма долга: строка в отчёте, число в превью SSE (Go float64) */
  debt_amount: string | number;
  /** Отформатированная сумма долга */
  debt_amount_formatted?: string;
  /** Исполнительский сбор: строка в отчёте, число в превью SSE */
  enforcement_fee: string | number;
  /** Отформатированный исполнительский сбор */
  enforcement_fee_formatted?: string;
  /** Размер алиментов: строка в отчёте, число в превью SSE */
  alimony_amount?: string | number;
  /** Отформатированный размер алиментов */
  alimony_amount_formatted?: string;
  /** Период взыскания */
  payment_period?: string;
  /** Отдел судебных приставов */
  bailiff_office?: string;
  /** Адрес ОСП */
  bailiff_address?: string;
  /** ФИО судебного пристава */
  bailiff_name?: string;
  /** Телефон для получения информации */
  bailiff_phone?: string;
  /** Регион */
  region?: string;
  /** UIN для оплаты */
  uin?: string;
  /** Статус (open/closed) */
  status?: string;
  /** Дело закрыто */
  is_closed?: boolean;
  /** Есть задолженность */
  has_debt?: boolean;
  /** Указан размер алиментов */
  has_alimony_amount?: boolean;
  /** Дата закрытия */
  closure_date?: string;
  /** Причина закрытия */
  closure_reason?: string;
  /** Дата включения в реестр */
  registry_date?: string;
}

export interface FSSPAlimonyDebtorResult extends ParserResult {
  full_name: string;
  birth_date: string;
  records_found: number;
  total_debt_amount: number;
  validation_date: string;
  data?: FsspAlimonyCaseRecord[];
}

/**
 * CommonResponse для ФССП: должники по алиментам (API Contract 2.4).
 */
export type FsspAlimonyDebtorCommonResponse = ComplianceCommonResponse<FsspAlimonyCaseRecord>;

export interface FNPPledgeResult extends ParserResult {
  full_name: string;
  birth_date: string;
  data?: {
    total_pledges: number;
    pledges: Array<{
      "Дата регистрации": string;
      "Номер уведомления о возникновении залога": string;
      Имущество: string;
      Залогодатель: string;
      Залогодержатель: string;
    }>;
  };
}

export interface TerroristExtremistResult extends ParserResult {
  full_name: string;
  birth_date: string;
  records_found: number;
  data?: Array<{
    "Фамилия, имя и отчество": string;
    "Дата рождения": string;
    "Место рождения": string;
  }>;
}

export interface CorruptionResult extends ParserResult {
  full_name: string;
  data?: Array<{
    "Фамилия, имя и отчество лица, которое было уволено (чьи полномочия были прекращены) в связи с утратой доверияза совершение коррупционного правонарушения": string;
    "Наименование органа (организации), в котором замещало должность лицо, которое было уволено(чьи полномочия были прекращены) в связис утратой доверия за совершение коррупционного правонарушения": string;
    "Наименование должности, замещаемой на момент увольнения (прекращение полномочий) в связис утратой доверия за совершение коррупционного правонарушения": string;
    "Положение нормативного правового акта, требования которого были нарушены и послужившее основанием для увольнения (прекращение полномочий) лица в связи с утратой доверия за совершение коррупционного правонарушения": string;
    "Дата соответствующего акта или решения суда об увольнении(о прекращении полномочий)в связи с утратой доверияза совершение коррупционного правонарушения": string;
    "Дата размещения информации на официальном сайте федеральной государственной информационной системы «Единая информационная система управления кадровым составом государственной гражданской службы Российской Федерации»": string;
  }>;
}

export interface FNSDisqualifiedResult extends ParserResult {
  full_name: string;
  birth_date: string;
  records_found: number;
  disqualification_active: boolean;
  data?: Array<{
    "Номер записи РДЛ": string;
    "Дисквалифицированное лицо": string;
    "Дата рождения": string;
    "Место рождения": string;
    Организация: string;
    ИНН: string;
    Должность: string;
    "Статья КоАП РФ": string;
    "Наименование органа, составившего протокол об административном правонарушении": string;
    Судья: string;
    "Должность судьи": string;
    Срок: string;
    "Дата начала": string;
    "Дата окончания": string;
  }>;
}

// ============================================================================
// FNS AU Violations Request/Response Types
// ============================================================================

/**
 * Запрос на поиск нарушений арбитражных управляющих ФНС.
 * Использует унифицированный ComplianceSearchRequest с обязательным tax_id в person_data.
 */
export type FnsAuViolationsRequest = ComplianceSearchRequest;

export interface FNSAUViolationsResult extends ParserResult {
  full_name: string;
  birth_date: string;
  tax_id: string;
  records_found: boolean;
  data?: Array<{
    "Наименование саморегулируемой организации арбитражных управляющих, членом которой является (являлся) арбитражный управляющий": string;
    "Фамилия арбитражного управляющего": string;
    "Имя арбитражного управляющего": string;
    "Отчество арбитражного управляющего": string;
    "ИНН арбитражного управляющего": string;
    "Дата решения суда": string;
    "Номер судебного дела": string;
    "Наименование суда, установившего нарушения в деятельности арбитражного управляющего": string;
    "Примененное наказание. Признание действий (бездействия) неправомерными. Устное замечание.": string;
    "Размер причиненных убытков": string;
    "Срок действия примененного наказания": string;
    "Наименование должника, в деле о банкротстве которого допущены нарушения": string;
    "Место нахождения должника": string;
  }>;
}

// ============================================================================
// Generic Compliance Request/Response Types
// ============================================================================

/**
 * Персональные данные физического лица для compliance-проверок.
 * Соответствует backend схеме PersonDataRequest.
 */
export interface PersonDataRequest {
  /** Фамилия */
  last_name?: string;
  /** Имя */
  first_name?: string;
  /** Отчество */
  middle_name?: string;
  /** Дата рождения (YYYY-MM-DD) */
  birth_date?: string;
  /** ИНН (10 или 12 цифр) */
  tax_id?: string;
  /** СНИЛС */
  ssn?: string;
  /** Серия паспорта */
  passport_series?: string;
  /** Номер паспорта */
  passport_number?: string;
  /** Дата выдачи паспорта */
  passport_issue_date?: string;
}

/**
 * Данные юридического лица для compliance-проверок.
 * Соответствует backend схеме LegalDataRequest.
 */
export interface LegalDataRequest {
  /** Название компании */
  company_name?: string;
  /** ИНН компании (10 цифр) */
  tax_id?: string;
  /** @deprecated Use tax_id instead */
  inn?: string;
  /** ОГРН компании (13 цифр) */
  ogrn?: string;
  /** КПП компании (9 цифр) */
  kpp?: string;
  /** Юридический адрес */
  legal_address?: string;
}

/**
 * Контактные данные для compliance-проверок.
 * Содержит телефоны, email, адреса, IP-адреса.
 * Соответствует backend схеме ContactDataRequest.
 */
export interface ContactDataRequest {
  /** Список телефонных номеров */
  phones?: string[];
  /** Основной телефонный номер */
  phone?: string;
  /** Список email адресов */
  emails?: string[];
  /** Основной email адрес */
  email?: string;
  /** Список адресов */
  addresses?: string[];
  /** Основной адрес */
  address?: string;
  /** Адрес регистрации */
  registration_address?: string;
  /** Фактический адрес */
  actual_address?: string;
  /** Список IP-адресов */
  ip_addresses?: string[];
  /** Основной IP-адрес */
  ip_address?: string;
}

/**
 * Унифицированный запрос на compliance-проверку.
 * Использует вложенную структуру person_data/contact_data/legal_data для соответствия backend.
 *
 * Соответствует backend схеме ComplianceSearchRequest.
 */
export interface ComplianceSearchRequest {
  /** Персональные данные физлица для поиска */
  person_data?: PersonDataRequest;
  /** Контактные данные (телефоны, email, адреса, IP-адреса) */
  contact_data?: ContactDataRequest;
  /** Данные юрлица для поиска */
  legal_data?: LegalDataRequest;
  /** Дополнительные параметры для конкретного модуля */
  module_options?: Record<string, unknown>;
  /** Уникальный ID запроса */
  request_id?: string;
  /** Источник запроса */
  source?: string;
}

/**
 * Базовый запрос поиска (соответствует backend SearchRequest).
 * Используется внутри ComplianceResult.search_request.
 */
export interface SearchRequest {
  /** Поисковый запрос (объединенное ФИО или другой идентификатор) */
  query: string;
  /** Полное ФИО */
  full_name?: string;
  /** Фамилия */
  last_name?: string;
  /** Имя */
  first_name?: string;
  /** Отчество */
  middle_name?: string;
  /** Дата рождения (YYYY-MM-DD) */
  birth_date?: string;
  /** ИНН */
  tax_id?: string;
  /** Номер документа */
  document_number?: string;
  /** Временная метка поиска */
  search_timestamp?: string;
  /** Источник данных */
  source?: string;
  /** Данные персоны (если вложено в person_data) */
  person_data?: {
    tax_id?: string;
    last_name?: string;
    first_name?: string;
    middle_name?: string;
    birth_date?: string;
    full_name?: string;
    [key: string]: string | undefined;
  };
  /** ID запроса */
  request_id?: string;
  /** Точное совпадение */
  exact_match?: boolean;
  /** Учитывать регистр */
  case_sensitive?: boolean;
  /** Максимальное количество результатов */
  max_results?: number;
}

/**
 * Метаданные запроса в CommonResponse.
 */
export interface ResponseMetadata {
  request_id: string;
  requested_at: string;
  completed_at: string;
  processing_time_ms: number;
  data_freshness: string;
  [key: string]: unknown;
}

/**
 * Метаданные API модуля в CommonResponse.
 */
export interface ApiModuleMetadata {
  module: string;
  version: string;
  parser_version: string;
  connection_type: string;
  retry_count: number;
  [key: string]: unknown;
}

/**
 * Базовая модель статистики результатов compliance-проверки.
 */
export interface BaseStatistics {
  total_results: number;
  unique_entities: number;
  categories: Record<string, unknown>;
  summary_flags?: Record<string, unknown>;
  date_range?: unknown;
}

/**
 * Статистика compliance-проверки (соответствует backend ComplianceStatistics).
 */
export interface ComplianceStatistics {
  /** Общее количество результатов */
  total_results: number;
  /** Количество уникальных сущностей */
  unique_entities: number;
  /** Категории результатов */
  categories: Record<string, unknown>;
  /** Флаги summary */
  summary_flags?: Record<string, unknown>;
  /** Диапазон дат */
  date_range?: {
    from?: string;
    to?: string;
  } | null;
  /** Дополнительные поля */
  [key: string]: unknown;
}

/**
 * Generic результат compliance-проверки (соответствует backend ComplianceResult).
 * Параметризуется типом записей (TRecord) и типом статистики (TStatistics).
 *
 * @template TRecord - тип записи результата (например, FnsAuViolationRecord)
 * @template TStatistics - тип статистики (например, AUViolationsStatistics)
 */
export interface ComplianceCommonResult<TRecord, TStatistics extends ComplianceStatistics = ComplianceStatistics> {
  /** Найдены ли записи */
  found: boolean;
  /** Общее количество записей */
  total_count: number;
  /** Параметры поиска (соответствует backend SearchRequest) */
  search_request: SearchRequest;
  /** Массив записей результата */
  results: TRecord[];
  /** Статистика по результатам */
  statistics: TStatistics;
  /** Профиль рисков (соответствует backend RiskProfile) */
  risk_profile: RiskProfile;
  /** Источник данных */
  source: string;
  /** Дата актуальности данных (YYYY-MM-DD) */
  data_actual_date: string;
  /** Время обработки в миллисекундах */
  processing_time_ms?: number;
  /** Сообщение об ошибке (если есть) */
  error_message?: string;
  /** Предупреждения */
  warnings?: string[];
  /** Результат верификации ФИО (для модулей с проверкой ФИО по ИНН) */
  fio_verification?: {
    verified: boolean;
    match_details?: {
      last_name?: boolean;
      first_name?: boolean;
      middle_name?: boolean;
    };
  };
}

/**
 * Generic ответ API для compliance-модулей (соответствует backend CommonResponse.to_dict()).
 * Параметризуется типом записей (TRecord) и типом статистики (TStatistics).
 *
 * @template TRecord - тип записи результата
 * @template TStatistics - тип статистики
 */
export interface ComplianceCommonResponse<TRecord, TStatistics extends ComplianceStatistics = ComplianceStatistics> {
  /** Статус выполнения (UPPERCASE по контракту API v2.3) */
  status: "COMPLETED" | "ERROR";
  /** Успешность выполнения запроса */
  success: boolean;
  /** Имя модуля */
  module_name: string;
  /** Данные результата проверки (соответствует backend ComplianceResult) */
  data: ComplianceCommonResult<TRecord, TStatistics>;
  /** Дата актуальности данных (YYYY-MM-DD) */
  data_actual_date: string;
  /** Метаданные запроса */
  metadata: ResponseMetadata;
  /** Метаданные API модуля */
  api_metadata: ApiModuleMetadata;
  /** Сообщение об ошибке (если есть) */
  error?: string;
}

/**
 * Статистика нарушений арбитражных управляющих FNS AU.
 *
 * В categories дополнительно содержатся поля по регионам, судам,
 * должникам и агрегированный материальный ущерб.
 */
export interface AUViolationsStatistics extends ComplianceStatistics {
  categories: {
    punishment_types?: Record<string, number>;
    court_names?: Record<string, number>;
    debtor_locations?: Record<string, number>;
    [key: string]: unknown;
  };
}

/**
 * Нормализованная запись о нарушении арбитражного управляющего
 * в формате CommonResponse (совместима с ViolationsResultList).
 */
export interface FnsAuViolationRecord {
  number: string;
  organization_name: string;
  last_name: string;
  first_name: string;
  middle_name: string;
  tax_id: string;
  case_number: string;
  decision_date: string;
  court_name: string;
  punishment: string;
  damage_amount: string;
  punishment_period: string;
  debtor_name: string;
  debtor_location: string;
}

/**
 * Результат проверки нарушений АУ в формате CommonResponse.
 * Type alias для generic ComplianceCommonResult.
 */
export type FnsAuCommonResult = ComplianceCommonResult<FnsAuViolationRecord, AUViolationsStatistics>;

/**
 * Общий ответ API для модуля FNS AU Violations (CommonResponse).
 * Type alias для generic ComplianceCommonResponse.
 */
export type FnsAuCommonResponse = ComplianceCommonResponse<FnsAuViolationRecord, AUViolationsStatistics>;

/**
 * Нормализованная запись о дисквалифицированном лице
 * в формате CommonResponse (совместима с DisqualifiedResultList).
 */
export interface FnsDisqualifiedRecord {
  record_number: string;
  disqualified_person: string;
  birth_date: string;
  birth_place?: string;
  organization?: string;
  organization_inn?: string;
  position?: string;
  article_koap?: string;
  authority_name?: string;
  judge_name?: string;
  judge_position?: string;
  term_duration?: string;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
}

/**
 * Статистика дисквалификаций FNS Disqualified.
 */
export interface DisqualifiedStatistics extends ComplianceStatistics {
  categories: {
    active?: number;
    expired?: number;
    [key: string]: unknown;
  };
}

/**
 * Результат проверки дисквалификаций в формате CommonResponse.
 * Type alias для generic ComplianceCommonResult.
 */
export type FnsDisqualifiedCommonResult = ComplianceCommonResult<FnsDisqualifiedRecord, DisqualifiedStatistics>;

/**
 * Общий ответ API для модуля FNS Disqualified.
 * Type alias для generic ComplianceCommonResponse.
 */
export type FnsDisqualifiedCommonResponse = ComplianceCommonResponse<FnsDisqualifiedRecord, DisqualifiedStatistics>;

/**
 * Нормализованная запись о криминальном розыске ФССП
 * в формате CommonResponse.
 * Соответствует FsspCriminalCase.to_dict() из backend.
 */
export interface FsspCriminalWantedRecord {
  id: string;
  type: string;
  case_id?: string;
  suspect_full_name: string;
  suspect_birth_date?: string;
  suspect_birth_place?: string;
  suspect_registration_place?: string;
  search_case_number?: string;
  search_object_type?: string;
  criminal_code_article?: string;
  crime_description?: string;
  crime_category?: string;
  search_date?: string;
  search_reason?: string;
  search_status?: string;
  search_level?: string;
  initiating_authority?: string;
  initiating_authority_contact?: string;
  search_authority?: string;
  contact_phone?: string;
  contact_address?: string;
  region?: string;
  federal_district?: string;
  physical_description?: string;
  last_seen_location?: string;
  additional_info?: string;
  data_update_date?: string;
  source_url?: string;
  is_active?: boolean;
  is_serious_crime?: boolean;
  has_contact_info?: boolean;
  crime_severity?: string;
  severity?: string;
}

/**
 * Нормализованная запись о розыске должников ФССП
 * в формате CommonResponse.
 *
 * Соответствует Value Object FsspDebtorWantedCase из бэкенда.
 */
export interface FsspDebtorWantedRecord {
  id: string;
  type: string;
  suspect_full_name: string;
  suspect_last_name: string;
  suspect_first_name: string;
  suspect_middle_name: string;
  suspect_birth_date: string;
  search_status: string;
  search_case_number: string;
  search_object_type: string; // Тип объекта розыска (должник — физическое лицо, должник — ЮЛ, и т.д.)
  search_date: string;
  enforcement_case_number: string;
  enforcement_category_code: string;
  enforcement_subject: string;
  initiator_office_code: string;
  initiator_office_name: string;
  initiator_official_full_name: string;
  initiator_official_position: string;
  territory_code: string;
  region: string;
  additional_info: string;
  additional_info_label: string;
  publishing_date: string;
  data_source: string;
  data_source_url: string;
  last_update_date: string;
  data_quality_notes: string;
  verification_status: string;
  is_active: boolean;
  raw_record_id: string;
  created_at: string;
}

/**
 * CommonResponse для модуля FSSP Debtor Wanted (API Contract 2.4).
 */
export type FsspDebtorWantedCommonResponse = ComplianceCommonResponse<FsspDebtorWantedRecord>;

/**
 * Статистика для модуля криминального розыска ФССП.
 */
export interface FsspCriminalWantedStatistics extends ComplianceStatistics {
  /** Количество записей (если отдает бэкенд; иначе выводится из total_count) */
  records_count?: number;
}

/**
 * Результат проверки криминального розыска ФССП в формате CommonResponse.
 * Type alias для generic ComplianceCommonResult.
 */
export type FsspCriminalWantedCommonResult = ComplianceCommonResult<
  FsspCriminalWantedRecord,
  FsspCriminalWantedStatistics
>;

/**
 * Общий ответ API для модуля FSSP Criminal Wanted (CommonResponse).
 * Type alias для generic ComplianceCommonResponse.
 */
export type FsspCriminalWantedCommonResponse = ComplianceCommonResponse<
  FsspCriminalWantedRecord,
  FsspCriminalWantedStatistics
>;

export interface EFRSBBankruptcyResult extends ParserResult {
  inn: string;
  data?: Array<{
    "Наименование должника": string;
    ИНН: string;
    "Дата вынесения решения о банкротстве": string;
    Суд: string;
    "Номер дела": string;
    "Стадия банкротства": string;
  }>;
}

export interface FSSPCriminalWantedResult extends ParserResult {
  full_name: string;
  birth_date: string;
  records_found: number;
  data?: Array<{
    "ФИО подозреваемого": string;
    "Дата рождения подозреваемого": string;
    "Место рождения подозреваемого": string;
    "Наименование органа инициировавшего розыск": string;
    "Контактная информация гос. органа, инициировавшего розыск": string;
    "Наименование гос. органа, осуществляющего розыск": string;
    "Статья УК РФ, по которой возбуждено дело": string;
  }>;
}

export interface MinjustForeignAgentsResult extends ParserResult {
  full_name: string;
  birth_date: string;
  inn?: string;
  snils?: string;
  records_found: number;
  data?: Array<{
    "Номер п/п": string;
    ФИО: string;
    "Основания для включения": string;
    "Дата включения в реестр": string;
    "Дата исключения из реестра": string;
    "Доменные имена": string;
    ИНН: string;
    СНИЛС: string;
    "Дата рождения": string;
    "Дата опубликования решения": string;
    "Номер специального счета": string;
    Банк: string;
    "БИК банка": string;
    "Корреспондентский счет": string;
    "Дата открытия счета": string;
    "Дата договора банковского счета": string;
  }>;
}

// Типы для парсеров в результатах
export type ParserResults = {
  fssp_debtor_wanted?: FSSPDebtorResult;
  fssp_criminal_wanted?: FSSPCriminalWantedResult;
  fns_disqualified?: FNSDisqualifiedResult;
  fns_au_violations?: FNSAUViolationsResult;
  fns_tax_debtor?: FNSTaxDebtorResult;
  fssp_ip_debtor?: FSSPIPDebtorResult;
  efrsb_bankruptcy?: EFRSBBankruptcyResult;
  fnp_pledges?: FNPPledgeResult;
  fsfm_terrorists?: TerroristExtremistResult;
  government_corruptioners?: CorruptionResult;
  minjust_foreign_agents?: MinjustForeignAgentsResult;
};

// Константы для парсеров
export const PARSER_CATEGORIES = {
  ADMINISTRATIVE_VIOLATIONS: "administrative_violations",
  CRIMINAL_AND_WANTED: "criminal_and_wanted",
  DEBTS_AND_PLEDGES: "debts_and_pledges",
  COURTS_AND_BANKRUPTCY: "courts_and_bankruptcy",
  NEGATIVE: "negative",
} as const;

export const PARSER_TYPES = {
  // Административные нарушения
  FNS_AU_VIOLATIONS: "fns_au_violations",
  FNS_DISQUALIFIED: "fns_disqualified",

  // Криминальные и розыскные базы
  FSSP_CRIMINAL_WANTED: "fssp_criminal_wanted",
  FSSP_DEBTOR_WANTED: "fssp_debtor_wanted",

  // Долги и залоги
  FNP_PLEDGES: "fnp_pledges",
  FNS_TAX_DEBTOR: "fns_tax_debtor",
  FNS_ACCOUNT_SUSPENSION: "fns_account_suspension",
  FSSP_IP_DEBTOR: "fssp_ip_debtor",

  // Суды и банкротство
  EFRSB_BANKRUPTCY: "efrsb_bankruptcy",

  // Негативные списки
  FSFM_TERRORISTS: "fsfm_terrorists",
  GOVERNMENT_CORRUPTIONERS: "government_corruptioners",
  MINJUST_FOREIGN_AGENTS: "minjust_foreign_agents",
} as const;

export const RISK_LEVELS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
} as const;

export const TASK_STATUSES = {
  PENDING: "PENDING",
  STARTED: "STARTED",
  PROGRESS: "PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;

// === ТИПЫ ДЛЯ ВАЛИДАЦИИ ТЕЛЕФОНОВ (PhoneDE) ===

/**
 * PhoneDE - обогащенный телефон в плоском формате.
 */
export interface PhoneDE {
  // Базовые поля
  source: string | null;
  value: string | null;

  // Классификация и форматы
  phone_type: string | null;
  value_formatted_7: string | null;
  value_formatted_8: string | null;

  // Компоненты номера
  country_code: string | null;
  city_code: string | null;
  number: string | null;
  extension: string | null;

  // Оператор
  provider: string | null;
  operator_brand: string | null;
  previous_provider: string | null;
  transit: string | null;

  // География
  country: string | null;
  country_name: string | null;
  federal_district: string | null;
  region: string | null;
  city: string | null;
  district: string | null;

  // Координаты и время
  timezone: string | null;
  latitude: string | null;
  longitude: string | null;

  // Статус
  status: string;
  status_check_timestamp: string | null;

  // OSINT данные
  osint_data: unknown | null;
  osint_lookup: OsintLookup | null;
  /** Флаг выполнения OSINT обогащения (для real-time отображения) */
  osint_enriching?: boolean;
  /** Количество завершённых OSINT модулей */
  osint_completed_modules?: number;
  /** Общее количество OSINT модулей */
  osint_total_modules?: number;
}

/**
 * Ответ API валидации телефона (CommonResponse формат).
 */
export type PhoneValidationResponse = CommonResponse<PhoneDE>;

// === ТИПЫ ДЛЯ ВАЛИДАЦИИ EMAIL (EmailDE) ===

/**
 * EmailDE - обогащенный email в плоском формате.
 * Плоская структура для валидации email адресов.
 */
export interface EmailDE {
  // Базовые поля
  source: string | null;
  value: string | null;
  local: string | null;
  domain: string | null;

  // Классификация домена
  domain_type: "PUBLIC" | "NONPUBLIC" | "DISPOSAL" | null;

  // MX записи
  mx_domains: string[];

  // Проверка существования
  exists: boolean | null;
  existance_timestamp: string | null;

  // Флаги валидации
  is_valid_format: boolean;
  is_disposable: boolean;
  is_public: boolean;
  is_corporate: boolean;
  has_plus_alias: boolean;
  has_dots: boolean;
  has_mx: boolean;

  // OSINT данные
  osint_data?: unknown | null;
  osint_lookup?: OsintLookup | null;
  /** Флаг выполнения OSINT обогащения (для real-time отображения) */
  osint_enriching?: boolean;
  /** Количество завершённых OSINT модулей */
  osint_completed_modules?: number;
  /** Общее количество OSINT модулей */
  osint_total_modules?: number;

  // Метаданные результата
  success: boolean;
  message?: string;
  processing_time_ms?: number;
  issues?: string[];
  recommendations?: string[];
}

/**
 * Ответ API валидации email (CommonResponse формат).
 */
export type EmailValidationResponse = CommonResponse<EmailDE>;

// === ТИПЫ ДЛЯ ВАЛИДАЦИИ ИНН (TaxIdDE) ===

/**
 * TaxIdDE - результат валидации ИНН.
 */
export interface TaxIdDE {
  // Базовые поля
  source: string;
  value: string;
  type: string;
  validity: boolean;
  is_valid: boolean;

  // Региональная информация
  region_code: string | null;
  region: string | null;

  // Информация об инспекции
  fns_inspection_code: string | null;
  fns_inspection: string | null;
  fns_inspection_address: string | null;

  // Валидация
  validation_status: string | null;
  has_issues: boolean | null;
  issues: string[] | null;
  recommendations: string[] | null;
  confidence: number | null;

  // Метаданные
  processing_time_ms: number;
  petrovna_used: boolean;
  database_used: boolean;
  verified: boolean | null;
  verification_timestamp: string | null;
}

/**
 * Ответ API валидации ИНН (CommonResponse формат).
 */
export type TaxIdValidationResponse = CommonResponse<TaxIdDE>;

// === ТИПЫ ДЛЯ ВАЛИДАЦИИ СНИЛС (SnilsDE) ===

/**
 * SnilsDE - результат валидации СНИЛС.
 */
export interface SnilsDE {
  // Базовые поля
  source: string;
  value: string;
  type: string;
  validity: boolean;

  // Верификация
  verified: boolean | null;
  verification_timestamp: string | null;
}

/**
 * Ответ API валидации СНИЛС (CommonResponse формат).
 */
export type SnilsValidationResponse = CommonResponse<SnilsDE>;

// === ТИПЫ ДЛЯ ВАЛИДАЦИИ ДОКУМЕНТОВ ===

/**
 * DocumentDE - результат валидации документа (паспорт).
 */
export interface DocumentDE {
  is_valid: boolean;
  document_type: string;
  series: string;
  number: string;
  series_number: string;
  message: string;
  processing_time_ms: number;

  // Дополнительные поля (если доступны)
  region_code?: string | null;
  region_name?: string | null;
  year_code?: string | null;
  passport_type?: string | null;
}

/**
 * Ответ API валидации документа (CommonResponse формат).
 */
export type DocumentValidationResponse = CommonResponse<DocumentDE>;

// === ТИПЫ ДЛЯ ВАЛИДАЦИИ IP-АДРЕСОВ (IPAddressDE) ===

/**
 * WhoisInfo - структурированные данные WHOIS.
 * Содержит распарсенную информацию из WHOIS ответа.
 */
export interface WhoisInfo {
  // Информация о сети
  network_range?: string;
  network_name?: string;
  network_status?: string;

  // Информация об организации
  org_name?: string;
  org_type?: string;
  org_address?: string;
  org_country?: string;
  org_phone?: string;
  org_fax?: string;

  // Контактная информация
  abuse_contact?: string;
  admin_contact?: string;
  tech_contact?: string;

  // AS информация
  asn?: string;
  route?: string;
  route_descr?: string;

  // Даты
  created_date?: string;
  last_modified?: string;

  // Источник
  source?: string;
}

/**
 * IPAddressDE - результат валидации IP-адреса.
 * Содержит информацию о геолокации, провайдере и WHOIS данных.
 */
export interface IPAddressDE {
  // Базовые данные
  source: string;
  version: number;
  address: string;
  is_private: boolean;
  is_loopback: boolean;
  is_multicast: boolean;
  is_reserved: boolean;
  is_public: boolean;

  // Геолокация
  country: string | null;
  country_name: string | null;
  city: string | null;
  city_id: string | null;
  latitude: number | null;
  longitude: number | null;
  timezone: string | null;
  has_geolocation: boolean;

  // Провайдер
  provider: string | null;
  provider_type: string | null;
  organization: string | null;

  // Дополнительные данные
  network: string | null;
  has_whois: boolean;
  whois_info: string | null;
  /** Структурированные WHOIS данные */
  whois_parsed: WhoisInfo | null;
  last_updated: string | null;
  confidence: number | null;
}

/**
 * Ответ API валидации IP-адреса (CommonResponse формат).
 */
export type IPAddressValidationResponse = CommonResponse<IPAddressDE>;

// === ТИПЫ ДЛЯ ВАЛИДАЦИИ АДРЕСОВ ===

/**
 * Компонент адреса (AddressComponent из AddressDE).
 */
export interface AddressComponent {
  short_name: string | null;
  full_name: string | null;
  value: string | null;
  fias_id: string | null;
  kladr_id: string | null;
  cadastrial_number: string | null;
  area: string | null;
  square_meter_price: string | null;
  price: string | null;
}

/** Правообладатель из ЕГРН (реестргос.рф). ФИО открыто; доля числом. */
export interface AddressCadastralOwner {
  name: string;
  type: string;
  share: number;
}

/** Кадастровая справка (реестргос.рф/ЕГРН) — обогащение результата валидации адреса. */
export interface AddressCadastral {
  cadastral_number: string;
  address: string;
  type: string;
  area: number;
  cost: number | null;
  cost_approval_date: string | null;
  date_registration: string | null;
  land_category: string | null;
  permitted_use: string | null;
  geo_lat: number | null;
  geo_lon: number | null;
  owners: AddressCadastralOwner[];
  restriction_count: number;
}

/**
 * AddressDE - обогащенный адрес в плоском формате.
 */
export interface AddressDE {
  source: string | null;
  value: string | null;
  pullenti_standardized: string | null;
  country: string | null;
  country_iso_code: string | null;
  postal_code: string | null;
  federal_district: string | null;
  region_code: string | null;
  region: AddressComponent | null;
  district: AddressComponent | null;
  city: AddressComponent | null;
  city_area: AddressComponent | null;
  city_district: AddressComponent | null;
  settlement: AddressComponent | null;
  street: AddressComponent | null;
  stead: AddressComponent | null;
  house: AddressComponent | null;
  block: AddressComponent | null;
  entrance: AddressComponent | null;
  floor: AddressComponent | null;
  flat: AddressComponent | null;
  timezone: string | null;
  geo_lat: string | null;
  geo_lon: string | null;
  fias_id: string | null;
  kladr_id: string | null;
  cadastrial_number: string | null;
  house_children_count: number | null;
  is_complete: boolean;
  has_coordinates: boolean;
  components_count: number;
  cadastral?: AddressCadastral | null;
}

/**
 * Результат валидации адреса.
 */
export interface AddressValidationResult {
  is_valid: boolean;
  status: string;
  confidence: number;
  risk_level: string;
  issues: string[];
  recommendations: string[];
  missing_components: string[];
  recognized_components: string[];
}

/**
 * Данные валидации адреса (поле data в CommonResponse).
 */
export interface AddressValidationData {
  original_address: string;
  address_type: string;
  validation_result: AddressValidationResult;
  address: AddressDE | null;
  metrics: {
    completeness_score: number;
    quality_score: number;
    standardization_score: number;
  };
  processing: {
    processing_time_ms: number;
    server_version: string | null;
    processed_at: string;
  };
}

/**
 * Ответ API валидации адреса (CommonResponse формат).
 */
export type AddressValidationResponse = CommonResponse<AddressValidationData>;

export interface AddressValidationRequest {
  address: string;
  region?: number | string;
  normalize?: boolean;
  include_coordinates?: boolean;
  include_additional_info?: boolean;
  strict_validation?: boolean;
  /** Кадастровые данные от DaData для обогащения Pullenti */
  cadastral_data?: {
    /** Кадастровый номер земельного участка */
    stead_cadnum?: string | null;
    /** Кадастровый номер дома */
    house_cadnum?: string | null;
    /** Кадастровый номер квартиры */
    flat_cadnum?: string | null;
    /** Площадь квартиры (м²) */
    flat_area?: string | null;
    /** Рыночная стоимость м² */
    square_meter_price?: string | null;
    /** Рыночная стоимость квартиры */
    flat_price?: string | null;
  };
}

// === ТИПЫ ДЛЯ СУДЕБНЫХ РЕШЕНИЙ (GENERAL COURTS) ===

export interface GeneralCourtsSearchRequest {
  surname: string;
  name: string;
  patronymic?: string;
  search_as_plaintiff?: boolean;
  search_as_defendant?: boolean;
  exact_match?: boolean;
}

export interface GeneralCourtsPartyInfo {
  name: string;
  role?: string;
  is_target_person: boolean;
}

export interface GeneralCourtsJudgment {
  case_number: string;
  uin?: string;
  case_start_date?: string;
  publish_date?: string;
  court_name: string;
  judge_name?: string;
  case_type?: string;
  case_stage?: string;
  plaintiffs: GeneralCourtsPartyInfo[];
  defendants: GeneralCourtsPartyInfo[];
  source_url?: string;
  target_person_role?: string;
}

export interface GeneralCourtsResult {
  search_request: GeneralCourtsSearchRequest;
  judgments: GeneralCourtsJudgment[];
  total_found: number;
  as_plaintiff_count: number;
  as_defendant_count: number;
  search_duration?: number;
  pages_processed: number;
  source: string;
  search_timestamp: string;
}

export interface GeneralCourtsResponse {
  success: boolean;
  found: boolean;
  results: GeneralCourtsResult;
}

// === НОВЫЕ ТИПЫ ДЛЯ API v2.2 ===

/**
 * Уровни риска (соответствует backend RiskLevel enum).
 * D_RED: 90-100 баллов - критический риск
 * C_ORANGE: 70-89 баллов - высокий риск
 * B_YELLOW: 40-69 баллов - средний риск
 * A_GREEN: 0-39 баллов - низкий риск
 *
 * Также поддерживает короткий формат: D, C, B, A для обратной совместимости.
 */
export type RiskLevel = "D_RED" | "C_ORANGE" | "B_YELLOW" | "A_GREEN" | "D" | "C" | "B" | "A";

/**
 * Объект связи риска (соответствует backend RiskRelation dataclass).
 * По контракту API v2.3 — ВСЕГДА объект с полными полями.
 */
export interface RiskRelation {
  /** Тип риска: "disqualification", "criminal", "debt", etc. */
  type: string;
  /** Весовой коэффициент: 1.0-2.0 */
  weight: number;
  /** Уверенность: 0.0-1.0 */
  confidence: number;
  /** Балл риска: 0.0-100.0 */
  score: number;
  /** Дата первого события: YYYY-MM-DD */
  first_seen?: string;
  /** Дата последнего события: YYYY-MM-DD */
  last_seen?: string;
  /** Период: "2023" или "2018-2023" */
  period?: string;
}

/**
 * Флаг риска (соответствует backend RiskFlag dataclass).
 * По контракту API v2.3 relation — ВСЕГДА объект RiskRelation.
 */
export interface RiskFlag {
  /** Источник риска (название модуля/базы) */
  source: string;
  /** Описание риска */
  value: string;
  /** Уровень риска (поддерживает D_RED/C_ORANGE/B_YELLOW/A_GREEN и D/C/B/A) */
  type: RiskLevel;
  /** Объект связи риска (по контракту API v2.3 — всегда объект) */
  relation: RiskRelation;
  /** Дата генерации флага */
  generated_at?: string;
  /** Дата истечения флага */
  expires_at?: string;
  /** Дополнительные метаданные */
  metadata?: Record<string, unknown>;
}

/**
 * Профиль рисков (соответствует backend RiskProfile dataclass).
 * Формат соответствует API CONTRACT v2.3.0 и backend RiskProfile.to_api_dict()
 */
export interface RiskProfile {
  /** Список флагов риска */
  flags: RiskFlag[];
  /** Общий балл риска */
  total_score: number;
  /** Общий уровень риска (полное название: A_GREEN, B_YELLOW, C_ORANGE, D_RED) */
  overall_level?: RiskLevel;
  /** Максимальный уровень риска (буква: A, B, C, D) */
  max_risk_level: RiskLevel;
  /** Количество флагов по уровням (ключи: A, B, C, D) */
  flags_by_level?: Record<string, number>;
  /** Количество флагов по источникам */
  flags_by_source?: Record<string, number>;
  /** Баллы по категориям риска */
  risk_categories?: Record<string, number>;
  /** Флаг высокого риска (D_RED или C_ORANGE) */
  is_high_risk?: boolean;
  /** Количество критических флагов (D_RED) */
  critical_flags_count?: number;
  /** Количество флагов высокого риска (D_RED + C_ORANGE) */
  high_risk_flags_count?: number;
}

/**
 * Alias for RiskProfile for backward compatibility with component interfaces.
 * Used in ComplianceResultCard and similar components.
 */
export type RiskAssessment = RiskProfile;

export interface ApiMetadata {
  search_strategy: string;
  api_version: string;
  [key: string]: unknown;
}

export interface ComplianceResponseData<T = unknown> {
  found: boolean;
  total_count: number;
  search_request: Record<string, unknown>;
  statistics: Record<string, unknown>;
  results: T[];
  source: string;
  data_actual_date: string;
  /** Профиль рисков по API Contract 2.3 */
  risk_profile?: RiskProfile | null;
  /** @deprecated Используйте risk_profile вместо risk_flags */
  risk_flags?: RiskFlag[];
  /** Дополнительные поля для TaxDebt */
  total_records?: number;
  tax_id?: string;
  validation_date?: string;
  total_debt_amount?: number;
  /** Дополнительные поля для GeneralCourts / ArbitrationCourts */
  data?: T[];
}

export interface ComplianceApiResponse<T = unknown> {
  task_id?: string;
  status: "success" | "error" | "completed";
  success: boolean;
  module_name: string;
  parser_type?: string;
  started_at?: string;
  processing_time_ms?: number;
  data: ComplianceResponseData<T>;
  metadata?: ApiMetadata;
  /** Метаданные запроса (CommonResponse формат) */
  data_actual_date?: string;
  /** Метаданные API модуля (CommonResponse формат) */
  api_metadata?: {
    module?: string;
    version?: string;
    parser_version?: string;
    connection_type?: string;
    retry_count?: number;
    [key: string]: unknown;
  };
  error?: string | null;
  message?: string;
}

// Обратная совместимость - старый формат API
export interface LegacyApiResponse<T = unknown> {
  success: boolean;
  found: boolean;
  results: T[];
  count: number;
  message?: string;
  search_time?: number;
  task_id?: string;
  error?: string;
}

// Union type для работы с обоими форматами
export type ApiResponseUnion<T = unknown> = ComplianceApiResponse<T> | LegacyApiResponse<T>;

// === ТИПЫ ДЛЯ СПЕЦИФИЧЕСКИХ МОДУЛЕЙ COMPLIANCE ===

export interface FsspDebtorResult {
  suspect_full_name: string;
  suspect_birth_date?: string;
  search_case_number?: string;
  search_object_type?: string;
  enforcement_case_number?: string;
  bailiff_office_info?: string;
  contact_phone?: string;
  search_date?: string;
  search_reason?: string;
  additional_info?: string;
  status?: string;
  [key: string]: string | undefined;
}

export interface FsspCriminalResult {
  wanted_person: string;
  birth_date?: string;
  case_number?: string;
  issuing_agency?: string;
  wanted_since?: string;
  category?: string;
  [key: string]: string | undefined;
}

/**
 * Запись об уголовном розыске ФСИН (из базы данных).
 */
export interface FsinCriminalWantedRecord {
  /** ID записи в БД */
  id?: number;
  /** ФИО разыскиваемого */
  full_name: string;
  /** Дата рождения */
  birth_date?: string;
  /** Текст ориентировки */
  orientation_text?: string;
  /** Территориальный орган ФСИН */
  territorial_organ?: string;
  /** Район / региональный признак */
  district?: string;
  /** URL фотографии */
  photo_url?: string;
  /** Имя файла фото */
  photo_filename?: string;
  /** URL страницы на сайте ФСИН */
  page_url?: string;
  /** Наличие фотографии */
  has_photo?: boolean;
  /** Признак наличия даты рождения */
  has_birth_date?: boolean;
  /** Факторы риска (если вычисляются) */
  risk_factors?: string[];
  created_at?: string;
  updated_at?: string;
}

/**
 * Результат проверки уголовного розыска ФСИН (Database Integration).
 */
export interface FsinCriminalResult {
  /** Найден ли человек в базе */
  found: boolean;
  /** Количество найденных записей */
  total_count: number;
  /** Записи о розыске */
  results?: FsinCriminalWantedRecord[];
  /** Источник данных */
  source?: string;
  /** Дата актуальности данных */
  data_actual_date?: string;
  [key: string]: unknown;
}

/**
 * Интерфейс красной карточки Интерпола.
 */
export interface InterpolRedNoticeRecord {
  id?: string;
  type?: string;
  full_name?: string;
  last_name?: string;
  first_name?: string;
  birth_date?: string;
  nationalities?: string | string[];
  sex?: string;
  place_of_birth?: string;
  arrest_warrants_issuing_country?: string;
  charge?: string;
  charge_translation?: string;
  physical_description?: string;
  distinguishing_marks?: string;
  languages_spoken?: string | string[];
  photo_url?: string;
  details_url?: string;
  height?: number;
  weight?: number;
  eye_color?: string;
  hair_color?: string;
  [key: string]: unknown;
}

/**
 * Результат проверки по красным карточкам Интерпола.
 */
export interface InterpolRedNoticeResult {
  /** Найден ли человек в международном розыске */
  found: boolean;
  /** Количество найденных записей */
  total_count: number;
  /** Записи о красных карточках */
  records?: InterpolRedNoticeRecord[];
  /** Статистика */
  statistics?: {
    total_results: number;
    unique_entities: number;
    categories?: Record<string, number>;
  };
  /** Источник данных */
  source?: string;
  /** Время обработки в миллисекундах */
  processing_time_ms?: number;
  /** Дата актуальности данных */
  data_actual_date?: string;
  /** Профиль рисков (API Contract 2.3) */
  risk_profile?: RiskProfile | null;
  /** @deprecated Используйте risk_profile вместо risk_flags */
  risk_flags?: RiskFlag[];
  [key: string]: unknown;
}

/**
 * CommonResponse для Interpol Red Notice (API Contract 2.4).
 * Результаты находятся в response.data.results.
 */
export type InterpolRedNoticeCommonResponse = ComplianceCommonResponse<InterpolRedNoticeRecord>;

/**
 * Интерфейс записи санкционного списка ООН (Interpol UN Sanctions).
 */
export interface InterpolUnSanctionRecord {
  id?: string;
  type?: string;
  entity_id?: string;
  last_name?: string;
  first_name?: string;
  full_name?: string;
  entity_type?: string; // "individual" | "entity"
  is_individual?: boolean;
  is_entity?: boolean;
  birth_date?: string;
  name_at_birth?: string;
  name_in_original_script?: string;
  sex?: string;
  place_of_birth?: string;
  nationalities?: string;
  primary_nationality?: string;
  languages_spoken?: string;
  address?: string;
  addresses?: string[];
  listed_on?: string; // Дата включения в список
  committee?: string; // Комитет ООН
  subsidiary_body?: string;
  reason_for_listing?: string;
  comments?: string;
  aliases?: string[];
  has_aliases?: boolean;
  passport_numbers?: string[];
  national_ids?: string[];
  identity_documents?: Array<Record<string, unknown>>;
  has_documents?: boolean;
  document_summary?: string;
  father_name?: string;
  father_forename?: string;
  mother_name?: string;
  mother_forename?: string;
  un_reference?: string;
  un_resolution?: string;
  un_reference_date?: string;
  profession?: string;
  purposes?: string[];
  associates?: string[];
  main_activity?: string;
  from_chamber_of_commerce?: string;
  legal_form?: string;
  registration_number?: string;
  administrators?: string[];
  formerly_known_as?: string;
  summary?: string;
  additional_information?: string;
  // Photo URLs
  photo_url?: string;
  photo_urls?: string[];
  images_url?: string;
  details_url?: string;
  // MinIO photo storage
  photo_minio_path?: string;
  photo_minio_url?: string;
  photo_minio_paths?: string[];
  photo_minio_urls?: string[];
  photo_etag?: string;
  photo_count?: number;
  has_photo?: boolean;
  [key: string]: unknown;
}

/**
 * Статистика для UN Sanctions.
 */
export interface InterpolUnSanctionsStatistics extends ComplianceStatistics {
  individuals?: number;
  entities?: number;
  individuals_percentage?: number;
  entities_percentage?: number;
  by_committee?: Record<string, number>;
  by_nationality?: Record<string, number>;
  by_reason_category?: Record<string, number>;
  with_aliases?: number;
  with_documents?: number;
  aliases_percentage?: number;
  documents_percentage?: number;
  by_listing_year?: Record<string, number>;
}

/**
 * CommonResponse для Interpol UN Sanctions (API Contract 2.4).
 * Санкционные списки ООН через Интерпол.
 */
export type InterpolUnSanctionsCommonResponse = ComplianceCommonResponse<
  InterpolUnSanctionRecord,
  InterpolUnSanctionsStatistics
>;

// === Interpol Yellow Notice (Жёлтые карточки - пропавшие без вести) ===

/**
 * Запись Yellow Notice (пропавшее лицо).
 * Соответствует backend InterpolYellowNotice.to_dict()
 */
export interface InterpolYellowNoticeRecord {
  id?: string;
  entity_id: string;
  type?: string;
  last_name: string;
  first_name: string;
  full_name?: string;
  birth_date?: string;
  birth_name?: string;
  missing_date?: string;
  place_of_disappearance?: string;
  place_of_birth?: string;
  country_of_birth?: string;
  nationalities?: string;
  sex?: string;
  age_at_disappearance?: number;
  current_estimated_age?: number;
  height?: number;
  weight?: number;
  eye_color?: string;
  hair_color?: string;
  languages_spoken?: string;
  physical_description?: string;
  distinguishing_marks?: string;
  circumstances?: string;
  date_of_event?: string;
  requesting_country?: string;
  father_forename?: string;
  mother_forename?: string;
  mother_name?: string;
  photo_url?: string;
  photo_urls?: string[];
  images_url?: string;
  details_url?: string;
  case_status?: string;
  is_minor?: boolean;
  is_child?: boolean;
  missing_duration_years?: number;
  has_photo?: boolean;
  has_distinguishing_marks?: boolean;
  raw_data?: Record<string, unknown>;
  // MinIO photo storage
  photo_minio_path?: string;
  photo_minio_url?: string;
  photo_minio_paths?: string[];
  photo_minio_urls?: string[];
  [key: string]: unknown;
}

/**
 * Статистика для Yellow Notice.
 */
export interface InterpolYellowNoticeStatistics extends ComplianceStatistics {
  children?: number;
  adults?: number;
  children_percentage?: number;
  by_nationality?: Record<string, number>;
  by_requesting_country?: Record<string, number>;
  by_sex?: Record<string, number>;
  by_age_group?: Record<string, number>;
  by_missing_year?: Record<string, number>;
  with_photo?: number;
  with_distinguishing_marks?: number;
  photo_percentage?: number;
  marks_percentage?: number;
  recent_cases?: number;
  recent_cases_percentage?: number;
}

/**
 * CommonResponse для Interpol Yellow Notice (API Contract 2.4).
 * Жёлтые карточки Интерпола - пропавшие без вести.
 */
export type InterpolYellowNoticeCommonResponse = ComplianceCommonResponse<
  InterpolYellowNoticeRecord,
  InterpolYellowNoticeStatistics
>;

export interface MinjustForeignAgentsNewResult {
  id?: string;
  entity_type?: string;
  full_name: string;
  birth_date?: string;
  inn?: string;
  snils?: string;
  sequence_number?: string;
  agent_type?: string;
  inclusion_basis?: string;
  inclusion_date?: string;
  exclusion_date?: string;
  publication_date?: string;
  is_active?: boolean;
  risk_factors?: string[];
  [key: string]: unknown;
}

/**
 * CommonResponse для реестра иностранных агентов Минюста (API Contract 2.4).
 */
export type MinjustForeignAgentsCommonResponse = ComplianceCommonResponse<MinjustForeignAgentsNewResult>;

export interface FnsTaxDebtorResult {
  /** Индекс документа */
  document_index?: string;
  /** Дата документа */
  document_date?: string;
  /** Тип начисления */
  accrual_type?: string;
  /** Основание начисления */
  accrual_basis?: string;
  /** Сумма задолженности (может быть строкой или числом) */
  tax_debt?: number | string;

  /** Дополнительные поля (могут присутствовать в разных версиях API) */
  debtor_name?: string;
  inn?: string;
  tax_id?: string;
  debt_amount?: string;
  publish_date?: string;

  [key: string]: unknown;
}

/**
 * CommonResponse для реестра налоговых должников ФНС (API Contract 2.4).
 */
export type FnsTaxDebtorCommonResponse = ComplianceCommonResponse<FnsTaxDebtorResult>;

// === FNS Account Suspension (Приостановление операций по счетам) — API Contract 2.4 ===

/**
 * Запись о приостановлении операций по счетам ФНС.
 * Соответствует backend FnsAccountSuspension.to_dict()
 */
export interface FnsAccountSuspensionRecord {
  /** Номер решения */
  decision_number: string;
  /** Дата решения */
  decision_date: string;
  /** Код налогового органа */
  tax_authority_code: string;
  /** БИК банка */
  bik: string;
  /** Название банка */
  bank_name?: string;
  /** Код основания приостановления (1-6) */
  suspension_reason?: string;
  /** Расшифровка основания приостановления */
  suspension_reason_description?: string;
  /** Сумма приостановления */
  amount?: string;
  /** ИНН налогоплательщика */
  tax_id?: string;
  /** Дата действующего приостановления (DATA) */
  data_actual_date?: string;
  /** Дата и время размещения в сервисе (DATABI) */
  data_publish_datetime?: string;
}

/**
 * CommonResponse для приостановления операций по счетам ФНС (API Contract 2.4).
 */
export type FnsAccountSuspensionCommonResponse = ComplianceCommonResponse<FnsAccountSuspensionRecord>;

// === EBC (кредитный рейтинг / скоринг) — API Contract 2.4 ===

export interface CreditFactor {
  type?: string;
  weight?: number;
  description?: string;
}

export interface CreditInquiry {
  date?: string;
  organization?: string;
  purpose?: string;
}

export interface CreditApplication {
  date?: string;
  organization?: string;
  status?: string;
  amount?: string;
}

export interface Lawsuit {
  case_number?: string;
  court?: string;
  date?: string;
  status?: string;
  amount?: string;
}

export interface CreditInfo {
  credit_type?: string;
  amount?: string;
  status?: string;
  open_date?: string;
  close_date?: string;
  overdue_days?: number;
}

export interface CreditTypeInfo {
  type: number;
  type_name?: string;
  open_status: number;
  closed_status: number;
}

export interface BankruptcyInfo {
  status?: number;
  description?: string;
}

export interface CreditRatingRecord {
  name: string;
  surname: string;
  patronymic: string;
  birth_date: string;
  passport: string;
  full_name: string;
  update_date?: string;
  score?: number;
  has_score: boolean;
  /** Отсутствует кредитная история (score = -1750) */
  has_no_credit_history?: boolean;
  factors: Array<string | CreditFactor>;
  inquiries: CreditInquiry[];
  applications: CreditApplication[];
  lawsuits: Lawsuit[];
  completed_lawsuits: Lawsuit[];
  credits: CreditInfo[];
  credit_type_infos?: CreditTypeInfo[];
  bankruptcy?: BankruptcyInfo;
  capabilities?: number;
  capabilities_description?: string;
  worst_overdue_for_all_credits?: number;
  worst_overdue_for_active_credits?: number;
  worst_overdue_for_all_credits_description?: string;
  worst_overdue_for_active_credits_description?: string;
  has_bad_debt_or_passed_to_collection?: boolean;
  last_credit_close_date?: string;
  total_inquiries: number;
  total_applications: number;
  total_lawsuits: number;
  has_active_credits: boolean;
  has_overdue: boolean;
  inquiries_7_days?: number;
  inquiries_14_days?: number;
  inquiries_30_days?: number;
  inquiries_all_time?: number;
  applications_14_days_approved?: number;
  applications_14_days_rejected?: number;
  applications_30_days_approved?: number;
  applications_30_days_rejected?: number;
  applications_60_days_approved?: number;
  applications_60_days_rejected?: number;
  applications_all_time_approved?: number;
  applications_all_time_rejected?: number;
  active_credits_count?: number;
  active_credits_as_guarantor?: number;
  total_credit_amount?: string;
  monthly_payment?: string;
  // Диапазоны (EBC API v2)
  active_credits_count_range?: string;
  total_credit_amount_range?: string;
  monthly_payment_range?: string;
  overdue_amount_range?: string;
  lawsuits_in_progress?: number;
  lawsuits_completed?: number;
}

export interface CreditRatingCategories {
  subjects_with_rating?: number;
  subjects_with_overdue?: number;
  average_rating?: number;
  [key: string]: number | undefined;
}

export interface CreditRatingStatistics extends ComplianceStatistics {
  categories: CreditRatingCategories;
}

export type CreditRatingCommonResponse = ComplianceCommonResponse<CreditRatingRecord, CreditRatingStatistics>;

export interface CreditScoringRecord {
  name: string;
  surname: string;
  patronymic: string;
  birth_date: string;
  full_name: string;
  status: string;
  score?: number;
  is_found: boolean;
  has_score?: boolean;
  /** Отсутствует кредитная история (score = -1750) */
  has_no_credit_history?: boolean;
  creditworthiness_description?: string;
  request_time: number;
  response_time: number;
  api_request_id?: string;
}

export type CreditScoringCommonResponse = ComplianceCommonResponse<CreditScoringRecord>;

export interface FnsSubsidiaryDefendantResult {
  /** Данные ответчика */
  defendant_organization_name?: string;
  defendant_last_name?: string;
  defendant_first_name?: string;
  defendant_middle_name?: string;
  defendant_full_name?: string;
  defendant_display_name?: string;
  defendant_tax_id?: string;
  is_defendant_individual?: boolean;

  /** Данные должника */
  debtor_organization_name?: string;
  debtor_display_name?: string;
  debtor_tax_id?: string;

  /** Данные судебного дела */
  case_number?: string;
  decision_date?: string;
  act_type?: string;
  court_name?: string;
  execution_status?: string;
  severity_score?: number;

  [key: string]: unknown;
}

/**
 * CommonResponse для реестра субсидиарной ответственности ФНС (API Contract 2.4).
 */
export type FnsSubsidiaryDefendantCommonResponse = ComplianceCommonResponse<FnsSubsidiaryDefendantResult>;

export interface FnsDisqualifiedNewResult {
  person_name: string;
  inn?: string;
  disqualification_date?: string;
  disqualification_period?: string;
  reason?: string;
  [key: string]: unknown;
}

export interface GeneralCourtsApiResult {
  case_number: string;
  court_name: string;
  case_type?: string;
  judge_name?: string;
  plaintiffs: string[];
  defendants: string[];
  publish_date?: string;
  [key: string]: unknown;
}

/**
 * Событие в рамках судебного дела.
 */
export interface GeneralCourtsCaseEvent {
  event_name: string;
  event_result?: string | null;
  event_grounds?: string | null;
  event_date?: string | null;
}

/**
 * Нормализованная запись судебного дела общих судов.
 * Соответствует backend GeneralCourtsCase (после сериализации).
 */
export interface GeneralCourtsRecord {
  case_number: string;
  court_name: string;
  case_type?: string | null;
  judge_name?: string | null;
  case_status?: string | null;
  uin?: string | null;
  case_start_date?: string | null;
  publish_date?: string | null;
  decision_date?: string | null;
  region?: string | null;
  region_name?: string | null;
  region_svg_code?: string | null;
  region_pullenti?: string | null;
  case_stage?: string | null;
  case_category?: string | null;
  match_type?: string | null;
  source_url?: string | null;
  decision_text?: string | null;
  decision_summary?: string | null;
  decision_resolution?: string | null;
  decision_file_url?: string | null;
  case_events?: GeneralCourtsCaseEvent[];
  plaintiffs?: Array<{
    name?: string;
    role?: string;
    is_target_person?: boolean;
    articles?: string;
    result?: string;
  }>;
  defendants?: Array<{
    name?: string;
    role?: string;
    is_target_person?: boolean;
    articles?: string;
    result?: string;
  }>;
  third_parties?: Array<{
    name?: string;
    role?: string;
    is_target_person?: boolean;
    articles?: string;
    result?: string;
  }>;
  representatives?: Array<{
    name?: string;
    role?: string;
    is_target_person?: boolean;
    articles?: string;
    result?: string;
  }>;
  [key: string]: unknown;
}

/**
 * Статистика по результатам поиска общих судов.
 */
export interface GeneralCourtsStatistics extends ComplianceStatistics {
  categories: Record<string, number>;
}

export type GeneralCourtsCommonResult = ComplianceCommonResult<GeneralCourtsRecord, GeneralCourtsStatistics>;

export type GeneralCourtsCommonResponse = ComplianceCommonResponse<GeneralCourtsRecord, GeneralCourtsStatistics>;

export interface ArbitrationCourtsResult {
  case_number: string;
  court_name: string;
  case_category?: string;
  plaintiffs: string[];
  defendants: string[];
  case_date?: string;
  [key: string]: unknown;
}

/**
 * Участник арбитражного дела.
 */
export interface ArbitrationCourtsParty {
  name?: string;
  role?: string;
  tax_id?: string;
  ogrn?: string;
  address?: string;
  is_target_person?: boolean;
}

/**
 * Нормализованная запись арбитражного дела.
 * Соответствует backend ArbitrationCourtsCase (после сериализации).
 */
export type ArbitrationCourtsParticipant = ArbitrationCourtsParty | string;

export interface ArbitrationCourtInstanceEvent {
  event_type?: string;
  event_id?: string;
  date?: string;
  publish_date?: string;
  additional_info?: string;
  file_url?: string;
  declarer?: string;
  declarer_inn?: string;
  claim_sum?: string;
  finish_event?: boolean;
  content_type?: string;
  comment?: string;
}

export interface ArbitrationCourtInstance {
  instance_id?: string;
  instance_number?: number;
  name?: string;
  start_date?: string;
  state?: string;
  court?: {
    code?: string;
    name?: string;
  };
  judges?: string[];
  decision_file?: {
    url?: string;
    name?: string;
    file_type?: string;
  };
  events?: ArbitrationCourtInstanceEvent[];
  decision_text?: string;
  decision_summary?: string;
}

export interface ArbitrationCourtsRecord {
  case_number: string;
  case_id?: string;
  /** Нормализованное название суда (если доступно) */
  court_name?: string;
  /** Legacy поле: иногда приходит как строка или объект с name */
  court?: string | { name?: string };
  case_type?: string;
  case_category?: string;
  case_status?: string;
  /** Нормализованная дата регистрации (если доступно) */
  registration_date?: string;
  /** Legacy поле: иногда приходит как start_date */
  start_date?: string;
  /** Legacy поле: иногда подробности лежат в instances */
  instances?: ArbitrationCourtInstance[];
  decision_date?: string;
  entry_into_force_date?: string;
  last_event_date?: string;
  judge_name?: string;
  claim_amount?: number | string;
  claim_currency?: string;
  /** Участники дела (иногда приходят как строки) */
  plaintiffs?: ArbitrationCourtsParticipant[];
  respondents?: ArbitrationCourtsParticipant[];
  thirds?: ArbitrationCourtsParticipant[];
  others?: ArbitrationCourtsParticipant[];
  match_type?: string;
  /** Уровень достоверности совпадения (HIGH/MEDIUM/LOW) */
  confidence?: string;
  /** Регион (если возвращается источником) */
  region_name?: string;
  /** Legacy поле: признак завершённого дела */
  finished?: boolean;
  source_url?: string;
  decision_text?: string;
  decision_summary?: string;
  is_bankruptcy?: boolean;
  is_active?: boolean;
  /** ИНН искомого лица (для сопоставления участников) */
  searched_person_inn?: string;
  /** ФИО искомого лица (для сопоставления участников) */
  searched_person_name?: string;
  [key: string]: unknown;
}

/**
 * Статистика по результатам поиска арбитражных дел.
 */
export interface ArbitrationCourtsStatistics extends ComplianceStatistics {
  categories: Record<string, number>;
  bankruptcy_cases?: number;
  active_cases?: number;
  total_claim_amount?: number;
}

export type ArbitrationCourtsCommonResult = ComplianceCommonResult<
  ArbitrationCourtsRecord,
  ArbitrationCourtsStatistics
>;

export type ArbitrationCourtsCommonResponse = ComplianceCommonResponse<
  ArbitrationCourtsRecord,
  ArbitrationCourtsStatistics
>;

/** Информация о публикаторе */
export interface EfrsbPublisherInfo {
  name: string;
  guid?: string | null;
  type?: string | null;
}

/** Публикация в ЕФРСБ */
export interface EfrsbPublication {
  guid: string;
  number: string;
  date_publish: string;
  type_name: string;
  act_type?: string | null;
  publication_type: string;
  is_annulled: boolean;
  is_locked: boolean;
  publisher?: EfrsbPublisherInfo | null;
}

/** Данные о банкроте */
export interface EfrsbBankrupt {
  guid: string;
  full_name: string;
  inn?: string;
  snils?: string;
  ogrnip?: string | null;
  birth_date?: string;
  birth_place?: string;
  address?: string;
  region?: string;
  category?: string;
  is_target_person?: boolean;
  /** Статус ИП: unknown, active, terminated_bankruptcy, terminated_other */
  entrepreneur_status?: string;
  /** Текстовое описание статуса ИП */
  entrepreneur_status_text?: string | null;
  /** Дата изменения статуса ИП */
  entrepreneur_status_date?: string | null;
  /** Активен ли в реестре */
  is_active_in_registry?: boolean | null;
}

/** Связанное дело о банкротстве */
export interface EfrsbLinkedCase {
  guid?: string;
  case_number: string;
  court_name?: string;
  status_name?: string;
}

/** Арбитражный управляющий */
export interface EfrsbArbitrManager {
  full_name: string;
  guid?: string | null;
  inn?: string | null;
  snils?: string | null;
  reg_number?: string | null;
  sro_name?: string | null;
}

/** Дело о банкротстве */
export interface EfrsbLegalCase {
  guid: string;
  case_number: string;
  status?: string;
  status_name?: string;
  status_date?: string | null;
  case_start_date?: string | null;
  /** Дата завершения/прекращения дела */
  case_end_date?: string | null;
  court_name?: string;
  arbitr_manager?: EfrsbArbitrManager | null;
  publications_count?: number;
  publications?: EfrsbPublication[];
  linked_cases?: EfrsbLinkedCase[];
}

/** Внесудебное банкротство */
export interface EfrsbExtrajudicialBankruptcy {
  guid: string;
  status?: string;
  status_name?: string;
  status_date?: string;
  start_date?: string;
  end_date?: string;
  mfc_name?: string;
  debt_amount?: number;
  creditors_count?: number;
}

/** Сообщение о намерении подать на банкротство */
export interface EfrsbIntentionMessage {
  guid: string;
  number?: string;
  date_publish?: string;
  type_name?: string;
  /** Устаревшее поле, для обратной совместимости */
  publisher?: string | null;
  /** Детальная информация о публикаторе */
  publisher_info?: EfrsbPublisherInfo | null;
  debtor_name?: string;
  debtor_inn?: string;
  message_type?: string;
}

/** Результат поиска банкротства ЕФРСБ */
export interface EfrsbBankruptcyNewResult {
  bankrupt: EfrsbBankrupt;
  legal_cases: EfrsbLegalCase[];
  extrajudicial_bankruptcy?: EfrsbExtrajudicialBankruptcy[];
  intention_messages?: EfrsbIntentionMessage[];
  has_active_case: boolean;
  total_cases_count: number;
  total_publications_count: number;
  earliest_case_date?: string;
}

/**
 * CommonResponse для модуля EFRSB Bankruptcy (API Contract 2.4).
 */
export type EfrsbBankruptcyCommonResponse = ComplianceCommonResponse<EfrsbBankruptcyNewResult>;

/**
 * Запись о розыске МВД (API Contract 2.3 Section 6.6)
 */
export interface MvdWantedRecord {
  id?: number;
  full_name: string;
  last_name: string;
  first_name: string;
  middle_name?: string;
  birth_date?: string;
  birth_year?: number;
  gender?: string;
  nationality?: string;
  photo_url?: string;
  wanted_by?: string;
  wanted_reason?: string;
  region?: string;
  special_signs?: string;
  contact_info?: string;
  birth_place?: string;
  [key: string]: unknown;
}

/**
 * Данные ответа MVD Wanted (API Contract 2.3 - ComplianceResult)
 */
export interface MvdWantedResponseData {
  found: boolean;
  total_count: number;
  search_request: SearchRequest;
  results: MvdWantedRecord[];
  statistics: ComplianceStatistics;
  risk_profile: RiskProfile;
  source: string;
  data_actual_date: string;
  processing_time_ms?: number;
  error_message?: string;
  warnings?: string[];
}

/**
 * CommonResponse для МВД: федеральный розыск (API Contract 2.4).
 */
export type MvdWantedCommonResponse = ComplianceCommonResponse<MvdWantedRecord>;

// ============================================================================
// MVD Controlled Persons (API Contract 2.4)
// ============================================================================

/**
 * Запрос для модуля МВД: реестр контролируемых лиц.
 */
export interface MvdControlledPersonsRequest {
  full_name: string;
  birth_date: string;
  foreign_passport: string;
  document_issue_date: string;
}

/**
 * Запись о проверке в реестре контролируемых лиц МВД.
 */
export interface MvdControlledPersonRecord {
  full_name: string;
  surname: string;
  name: string;
  patronymic?: string;
  birth_date: string;
  passport_series?: string;
  passport_number: string;
  document_issue_date: string;
  nationality: string;
  is_in_registry: boolean;
  check_date: string;
  inclusion_reason?: string;
  restrictions?: string[];
  additional_info?: string;
}

/**
 * Статистика проверки контролируемых лиц МВД.
 */
export interface MvdControlledPersonsStatistics extends ComplianceStatistics {
  total_checked: number;
  found_in_registry: number;
  not_in_registry: number;
  match_rate: number;
  nationalities_breakdown?: Record<string, number>;
  restrictions_breakdown?: Record<string, number>;
}

export type MvdControlledPersonsCommonResponse = ComplianceCommonResponse<
  MvdControlledPersonRecord,
  MvdControlledPersonsStatistics
>;

// ============================================================================
// FNP Pledges (API Contract 2.4) — VO для response.data.results
// ============================================================================

/**
 * Участник залога (залогодатель или залогодержатель).
 */
export interface PledgeParticipant {
  participant_type: "person" | "organization";
  name: string;
  inn?: string;
  ogrn?: string;
}

/**
 * Запись о залоге движимого имущества (ФНП).
 */
export interface FnpPledgeRecord {
  register_date: string;
  number: string;
  is_active: boolean;
  status: string;
  search_person_role: string;
  objects: string[];
  pledgors: PledgeParticipant[];
  pledgees: PledgeParticipant[];
  pledgor_hidden?: boolean;
  pledgee_hidden?: boolean;
}

/**
 * Статистика по результатам поиска залогов ФНП.
 * Дополнительные поля могут отличаться по версиям backend.
 */
export interface FnpPledgesStatistics extends ComplianceStatistics {
  active_records_count?: number;
  terminated_records_count?: number;
  search_person_as_pledgor_count?: number;
  search_person_as_pledgee_count?: number;
  total_objects?: number;
  search_time?: number;
}

export type FnpPledgesCommonResponse = ComplianceCommonResponse<FnpPledgeRecord, FnpPledgesStatistics>;

// ============================================================================
// FSFM Terrorists / Government Corruptioners (VO)
// ============================================================================

/**
 * Запись из перечня террористов и экстремистов (Росфинмониторинг).
 */
export interface FsfmTerroristRecord {
  id: string;
  full_name: string;
  birth_date?: string;
  birth_place?: string;
  entity_type: "person" | "organization";
  source_text?: string;
  legal_name?: string;
  tax_id?: string;
  registration_id?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Совпадение в международных санкционных списках (OFAC/EU/UK/UN/SECO/...).
 * Соответствует backend SanctionMatch.to_dict().
 */
export interface SanctionsScreeningRecord {
  /** Код списка-источника (OFAC_SDN, EU_CFSP, UN_SC, UK_OFSI, CH_SECO, ...) */
  source_list: string;
  /** Нативный идентификатор записи в источнике */
  source_uid: string;
  /** Тип сущности: person | legal | vessel | aircraft */
  entity_type: string;
  /** Основное наименование подсанкционной сущности */
  primary_name: string;
  /** Санкционная программа */
  list_program?: string | null;
  /** Дата включения в список */
  inclusion_date?: string | null;
  /** Дата исключения (null = активна) */
  exclusion_date?: string | null;
  /** Активна ли запись в списке */
  is_active?: boolean;
  /** Основание/примечание */
  remarks?: string | null;
  /** Псевдоним, по которому найдено совпадение */
  matched_alias?: string;
  /** Тип псевдонима: primary | strong | weak */
  matched_alias_type?: string;
  /** Поле, давшее совпадение (name | company_name | tax_id | passport | ...) */
  matched_field?: string;
  /** Тип совпадения: strong | possible */
  match_type?: "strong" | "possible";
  /** Итоговая уверенность 0..1 */
  score?: number;
  /** Атрибуты (ДР, гражданство, документы) */
  attributes?: Array<{ attr_type: string; attr_value: string; country_code?: string | null }>;
  /** Детали скоринга */
  score_details?: Record<string, unknown>;
}

/**
 * Совпадение в глобальном реестре PEP (OpenSanctions, набор peps).
 * Соответствует backend opensanctions.PEPRecord.
 */
export interface PepScreeningRecord {
  /** Идентификатор сущности в OpenSanctions */
  entity_id: string;
  /** Основное имя записи */
  full_name: string;
  /** Написание имени, по которому найдено совпадение */
  matched_name: string;
  /** Сам политически значимый человек */
  is_pep: boolean;
  /** Родственник или близкий партнёр политика (RCA) */
  is_rca: boolean;
  /** Должности с периодами */
  positions: string[];
  birth_date: string | null;
  birth_year: number | null;
  birth_place: string | null;
  gender: string | null;
  /** Страны гражданства и присутствия (ISO alpha-2) */
  countries: string[];
  /** Темы OpenSanctions (role.pep, role.rca, ...) */
  topics: string[];
  /** Исходные списки, утверждающие факт */
  datasets: string[];
  notes: string[];
  source_urls: string[];
  /** Страница сущности на сайте источника */
  entity_url: string;
  /** Совпадение подтверждено датой рождения запроса */
  birth_date_matched: boolean;
  risk_factors: string[];
}

/**
 * Запись из реестра уволенных в связи с утратой доверия.
 * Новый формат API (2.3+) с упрощенными полями.
 */
export interface GovernmentCorruptionRecord {
  id: number;
  record_number: string;
  full_name: string;
  organization: string;
  position?: string;
  legal_basis?: string;
  dismissal_date?: string;
  publication_date?: string;
  is_active: boolean;
  risk_factors?: string[];
}

// === CBR Warning List (Предупредительный список ЦБ РФ) ===

/**
 * Запись о физлице в Warning List ЦБ РФ (учредители/руководители).
 */
export interface CbrWarningListFlRecord {
  id?: number;
  cbr_id: string;
  tax_id: string;
  tax_id_hash?: string;
  full_name: string;
  full_name_hash?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  role: "founder" | "manager" | string;
  role_display?: string;
  position?: string; // Должность (для руководителей)
  share_percent?: number;
  share_nominal?: number;
  listed_date?: string; // Дата включения в список
  is_active?: boolean; // Активность записи
  last_seen_date?: string; // Дата последнего обновления
  risk_factors?: string[]; // Факторы риска
  organization_name?: string;
  organization_inn?: string;
  organization_signs?: string[];
  organization_regions?: string[];
  data_source?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Запись об организации в Warning List ЦБ РФ.
 */
export interface CbrWarningListOrgRecord {
  id?: number;
  cbr_id: string;
  name_org: string;
  inn?: string;
  addr?: string;
  signs?: string[];
  regions?: string[];
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Статистика CBR Warning List.
 */
export interface CbrWarningListStatistics extends ComplianceStatistics {
  categories: {
    founders?: number;
    managers?: number;
    organizations?: number;
    [key: string]: number | undefined;
  };
}

export type CbrWarningListFlCommonResponse = ComplianceCommonResponse<CbrWarningListFlRecord, CbrWarningListStatistics>;

export type CbrWarningListOrgCommonResponse = ComplianceCommonResponse<
  CbrWarningListOrgRecord,
  CbrWarningListStatistics
>;

// === FNS Mass Leaders (Массовые руководители ФНС) ===

/**
 * Запись о массовом руководителе ФНС.
 * Фактически возвращается backend (см. backend Value Object).
 */
export interface FnsMassLeadersRecord {
  id: string;
  organization_count: number;
  risk_level: "critical" | "high" | "medium" | "low";
  tax_id?: string;
  first_seen?: string;
  last_seen?: string;
  period?: string;
  full_name?: string;
  verified_full_name?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Статистика массовых руководителей.
 */
export interface FnsMassLeadersStatistics extends ComplianceStatistics {
  categories: {
    mass_leaders?: number;
    total_organizations?: number;
    [key: string]: number | undefined;
  };
}

export type FnsMassLeadersCommonResponse = ComplianceCommonResponse<FnsMassLeadersRecord, FnsMassLeadersStatistics>;

// === FNS Mass Founders (Массовые учредители ФНС) ===

/**
 * Запись о массовом учредителе ФНС.
 * Фактически возвращается backend (см. backend Value Object).
 */
export interface FnsMassFoundersRecord {
  id: string;
  organization_count: number;
  risk_level: "critical" | "high" | "medium" | "low";
  tax_id?: string;
  full_name?: string;
  verified_full_name?: string;
  first_seen?: string;
  last_seen?: string;
  period?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Статистика массовых учредителей.
 */
export interface FnsMassFoundersStatistics extends ComplianceStatistics {
  categories: {
    mass_founders?: number;
    total_organizations?: number;
    [key: string]: number | undefined;
  };
}

export type FnsMassFoundersCommonResponse = ComplianceCommonResponse<FnsMassFoundersRecord, FnsMassFoundersStatistics>;

// === НОВЫЕ КОНСТАНТЫ ДЛЯ API v2.2 ===

/**
 * Module keys для unified modules API (/v2/modules/).
 * Формат: compliance.{module_name}
 *
 * Используются для:
 * - POST /v2/modules/{module_key}/execute_async
 * - POST /v2/modules/{module_key}/execute
 * - GET /v2/modules/{module_key}
 */
export const COMPLIANCE_MODULES = {
  // Administrative violations
  FNS_DISQUALIFIED: "compliance.rus.fns_disqualified",
  FNS_AU_VIOLATIONS: "compliance.rus.fns_au_violations",

  // Criminal and wanted
  FSSP_DEBTOR_WANTED: "compliance.rus.fssp_debtor_wanted",
  FSSP_CRIMINAL_WANTED: "compliance.rus.fssp_criminal_wanted",
  FSIN_CRIMINAL_WANTED: "compliance.rus.fsin_criminal_wanted",
  MVD_WANTED: "compliance.rus.mvd_wanted",
  INTERPOL_RED_NOTICE: "compliance.global.interpol_red_notice",
  INTERPOL_UN_SANCTIONS: "compliance.global.interpol_un_sanctions",
  INTERPOL_YELLOW_NOTICE: "compliance.global.interpol_yellow_notice",

  // Debts and pledges
  FNS_TAX_DEBTOR: "compliance.rus.fns_tax_debtor",
  FNS_ACCOUNT_SUSPENSION: "compliance.rus.fns_account_suspension",
  FSSP_IP_DEBTOR: "compliance.rus.fssp_ip_debtor",
  FSSP_ALIMONY_DEBTOR: "compliance.rus.fssp_alimony_debtor",
  FNP_PLEDGES: "compliance.rus.fnp_pledges",
  CREDIT_RATING: "compliance.rus.credit_rating",
  CREDIT_SCORING: "compliance.rus.credit_scoring",

  // Courts and bankruptcy
  GENERAL_COURTS: "compliance.rus.general_courts",
  ARBITRATION_COURTS: "compliance.rus.arbitration_courts",
  EFRSB_BANKRUPTCY: "compliance.rus.efrsb_bankruptcy",
  FNS_SUBSIDIARY_DEFENDANT: "compliance.rus.fns_subsidiary_defendant",

  // Negative lists
  SANCTIONS_SCREENING: "compliance.global.sanctions_screening",
  PEP_SCREENING: "compliance.global.pep_screening",
  MINJUST_FOREIGN_AGENTS: "compliance.rus.minjust_foreign_agents",
  FSFM_TERRORISTS: "compliance.rus.fsfm_terrorists",
  GOVERNMENT_CORRUPTIONERS: "compliance.rus.government_corruptioners",
  CBR_WARNING_LIST: "compliance.rus.cbr_warning_list",
  CBR_WARNING_LIST_FL: "compliance.rus.cbr_warning_list_fl",
  FNS_MASS_LEADERS: "compliance.rus.fns_mass_leaders",
  FNS_MASS_FOUNDERS: "compliance.rus.fns_mass_founders",

  // Migration
  MVD_CONTROLLED_PERSONS: "compliance.rus.mvd_controlled_persons",
} as const;

export type ComplianceModule = (typeof COMPLIANCE_MODULES)[keyof typeof COMPLIANCE_MODULES];

/** Module keys валидационных модулей. */
export const VALIDATION_MODULES = {
  NAME: "validation.rus.name",
  PHONE: "validation.global.phone",
  EMAIL: "validation.global.email",
  ADDRESS: "validation.rus.address",
  TAX_ID: "validation.rus.tax_id",
  SNILS: "validation.rus.social_security_number",
  DOCUMENT: "validation.rus.document",
  IP_ADDRESS: "validation.global.ip_address",
} as const;

export type ValidationModule = (typeof VALIDATION_MODULES)[keyof typeof VALIDATION_MODULES];

/** Module keys верификационных модулей. */
export const VERIFICATION_MODULES = {
  FNS_TAX_ID: "verification.rus.fns_tax_id",
  SFR_SSN: "verification.rus.sfr_ssn",
  MVD_PASSPORT: "verification.rus.mvd_passport",
  MVD_DRIVER_LICENSE: "verification.rus.mvd_driver_license",
  OBRNADZOR_EDUCATION: "verification.rus.obrnadzor_education",
} as const;

export type VerificationModule = (typeof VERIFICATION_MODULES)[keyof typeof VERIFICATION_MODULES];

/** Module keys OSINT-модулей. */
export const OSINT_MODULES = {
  ENRICH_CONTACT: "osint.rus.enrich_contact",
} as const;

export type OsintModule = (typeof OSINT_MODULES)[keyof typeof OSINT_MODULES];

/** Module keys бизнес-модулей. */
export const BUSINESS_MODULES = {
  LEGAL_ENTITY_ENRICHMENT: "business.rus.legal_entity_enrichment",
  LEGAL_REPORT: "business.rus.legal_report",
  PERSON_CONNECTIONS: "business.rus.person_connections",
  PERSON_BUSINESS_CHECK: "business.rus.person_business_check",
  FINANCIAL_SCORE: "business.rus.financial_score",
} as const;

export type BusinessModule = (typeof BUSINESS_MODULES)[keyof typeof BUSINESS_MODULES];

/** Объединённый тип ключей модулей. */
export type ModuleKey = ValidationModule | VerificationModule | ComplianceModule | OsintModule | BusinessModule;

export const RISK_FLAG_TYPES = {
  POSITIVE: "positive",
  WARNING: "warning",
  THREATENING: "threatening",
} as const;

export type RiskFlagType = (typeof RISK_FLAG_TYPES)[keyof typeof RISK_FLAG_TYPES];

// === UTILITY TYPES ===

export type ComplianceResults = {
  [COMPLIANCE_MODULES.FSSP_DEBTOR_WANTED]: FsspDebtorResult;
  [COMPLIANCE_MODULES.FSSP_CRIMINAL_WANTED]: FsspCriminalResult;
  [COMPLIANCE_MODULES.FSIN_CRIMINAL_WANTED]: FsinCriminalResult;
  [COMPLIANCE_MODULES.MVD_WANTED]: MvdWantedResponseData;
  [COMPLIANCE_MODULES.MINJUST_FOREIGN_AGENTS]: MinjustForeignAgentsNewResult;
  [COMPLIANCE_MODULES.FNS_TAX_DEBTOR]: FnsTaxDebtorResult;
  [COMPLIANCE_MODULES.FNS_DISQUALIFIED]: FnsDisqualifiedNewResult;
  [COMPLIANCE_MODULES.GENERAL_COURTS]: GeneralCourtsApiResult;
  [COMPLIANCE_MODULES.ARBITRATION_COURTS]: ArbitrationCourtsResult;
  [COMPLIANCE_MODULES.EFRSB_BANKRUPTCY]: EfrsbBankruptcyNewResult;
};

/**
 * Типизированный маппинг: module -> record (VO) в формате CommonResponse.
 *
 * Используется на фронте для строгой типизации response/result/records.
 */
export type ComplianceRecordByModule = {
  // Administrative violations
  [COMPLIANCE_MODULES.FNS_DISQUALIFIED]: FnsDisqualifiedRecord;
  [COMPLIANCE_MODULES.FNS_AU_VIOLATIONS]: FnsAuViolationRecord;

  // Criminal and wanted
  [COMPLIANCE_MODULES.FSSP_DEBTOR_WANTED]: FsspDebtorWantedRecord;
  [COMPLIANCE_MODULES.FSSP_CRIMINAL_WANTED]: FsspCriminalWantedRecord;
  [COMPLIANCE_MODULES.FSIN_CRIMINAL_WANTED]: FsinCriminalWantedRecord;
  [COMPLIANCE_MODULES.MVD_WANTED]: MvdWantedRecord;
  [COMPLIANCE_MODULES.INTERPOL_RED_NOTICE]: InterpolRedNoticeRecord;
  [COMPLIANCE_MODULES.INTERPOL_UN_SANCTIONS]: InterpolUnSanctionRecord;
  [COMPLIANCE_MODULES.INTERPOL_YELLOW_NOTICE]: InterpolYellowNoticeRecord;

  // Debts and pledges
  [COMPLIANCE_MODULES.FNS_TAX_DEBTOR]: FnsTaxDebtorResult;
  [COMPLIANCE_MODULES.FNS_ACCOUNT_SUSPENSION]: FnsAccountSuspensionRecord;
  [COMPLIANCE_MODULES.FSSP_IP_DEBTOR]: FsspIpDebtorRecord;
  [COMPLIANCE_MODULES.FSSP_ALIMONY_DEBTOR]: FsspAlimonyCaseRecord;
  [COMPLIANCE_MODULES.FNP_PLEDGES]: FnpPledgeRecord;
  [COMPLIANCE_MODULES.CREDIT_RATING]: CreditRatingRecord;
  [COMPLIANCE_MODULES.CREDIT_SCORING]: CreditScoringRecord;

  // Courts and bankruptcy
  [COMPLIANCE_MODULES.GENERAL_COURTS]: GeneralCourtsRecord;
  [COMPLIANCE_MODULES.ARBITRATION_COURTS]: ArbitrationCourtsRecord;
  [COMPLIANCE_MODULES.EFRSB_BANKRUPTCY]: EfrsbBankruptcyNewResult;
  [COMPLIANCE_MODULES.FNS_SUBSIDIARY_DEFENDANT]: FnsSubsidiaryDefendantResult;

  // Negative lists
  [COMPLIANCE_MODULES.SANCTIONS_SCREENING]: SanctionsScreeningRecord;
  [COMPLIANCE_MODULES.PEP_SCREENING]: PepScreeningRecord;
  [COMPLIANCE_MODULES.MINJUST_FOREIGN_AGENTS]: MinjustForeignAgentsNewResult;
  [COMPLIANCE_MODULES.FSFM_TERRORISTS]: FsfmTerroristRecord;
  [COMPLIANCE_MODULES.GOVERNMENT_CORRUPTIONERS]: GovernmentCorruptionRecord;
  [COMPLIANCE_MODULES.CBR_WARNING_LIST]: CbrWarningListFlRecord;
  [COMPLIANCE_MODULES.CBR_WARNING_LIST_FL]: CbrWarningListFlRecord;
  [COMPLIANCE_MODULES.FNS_MASS_LEADERS]: FnsMassLeadersRecord;
  [COMPLIANCE_MODULES.FNS_MASS_FOUNDERS]: FnsMassFoundersRecord;

  // Migration
  [COMPLIANCE_MODULES.MVD_CONTROLLED_PERSONS]: MvdControlledPersonRecord;
};

export type ComplianceCommonResponseByModule<M extends ComplianceModule> = ComplianceCommonResponse<
  ComplianceRecordByModule[M]
>;

export type ComplianceCommonResultByModule<M extends ComplianceModule> = ComplianceCommonResult<
  ComplianceRecordByModule[M]
>;

/**
 * Типизированный маппинг: module -> request.
 *
 * Важно: разные модули реально требуют разные входные данные.
 * Используем это, чтобы фронт был строгим, а не «везде SearchFormData».
 */
export type ComplianceRequestByModule = {
  // Administrative violations
  [COMPLIANCE_MODULES.FNS_DISQUALIFIED]: SearchFormData;
  [COMPLIANCE_MODULES.FNS_AU_VIOLATIONS]: ComplianceSearchRequest;

  // Criminal and wanted
  [COMPLIANCE_MODULES.FSSP_DEBTOR_WANTED]: SearchFormData;
  [COMPLIANCE_MODULES.FSSP_CRIMINAL_WANTED]: SearchFormData;
  [COMPLIANCE_MODULES.FSIN_CRIMINAL_WANTED]: SearchFormData;
  [COMPLIANCE_MODULES.MVD_WANTED]: SearchFormData;
  [COMPLIANCE_MODULES.INTERPOL_RED_NOTICE]: SearchFormData;
  [COMPLIANCE_MODULES.INTERPOL_UN_SANCTIONS]: SearchFormData;
  [COMPLIANCE_MODULES.INTERPOL_YELLOW_NOTICE]: SearchFormData;

  // Debts and pledges
  [COMPLIANCE_MODULES.FNS_TAX_DEBTOR]: SearchFormData;
  [COMPLIANCE_MODULES.FNS_ACCOUNT_SUSPENSION]: { tax_id: string };
  [COMPLIANCE_MODULES.FSSP_IP_DEBTOR]: PersonData;
  [COMPLIANCE_MODULES.FSSP_ALIMONY_DEBTOR]: PersonData;
  [COMPLIANCE_MODULES.FNP_PLEDGES]: ComplianceSearchRequest;
  [COMPLIANCE_MODULES.CREDIT_RATING]: PersonData;
  [COMPLIANCE_MODULES.CREDIT_SCORING]: ComplianceSearchRequest;

  // Courts and bankruptcy
  [COMPLIANCE_MODULES.GENERAL_COURTS]: SearchFormData;
  [COMPLIANCE_MODULES.ARBITRATION_COURTS]: SearchFormData;
  [COMPLIANCE_MODULES.EFRSB_BANKRUPTCY]: SearchFormData;
  [COMPLIANCE_MODULES.FNS_SUBSIDIARY_DEFENDANT]: string;

  // Negative lists
  [COMPLIANCE_MODULES.SANCTIONS_SCREENING]: ComplianceSearchRequest;
  [COMPLIANCE_MODULES.PEP_SCREENING]: ComplianceSearchRequest;
  [COMPLIANCE_MODULES.MINJUST_FOREIGN_AGENTS]: SearchFormData;
  [COMPLIANCE_MODULES.FSFM_TERRORISTS]: ComplianceSearchRequest;
  [COMPLIANCE_MODULES.GOVERNMENT_CORRUPTIONERS]: ComplianceSearchRequest;
  [COMPLIANCE_MODULES.CBR_WARNING_LIST]: ComplianceSearchRequest;
  [COMPLIANCE_MODULES.CBR_WARNING_LIST_FL]: ComplianceSearchRequest;
  [COMPLIANCE_MODULES.FNS_MASS_LEADERS]: ComplianceSearchRequest;
  [COMPLIANCE_MODULES.FNS_MASS_FOUNDERS]: ComplianceSearchRequest;

  // Migration
  [COMPLIANCE_MODULES.MVD_CONTROLLED_PERSONS]: MvdControlledPersonsRequest;
};

export interface SearchFormData {
  last_name: string;
  first_name: string;
  middle_name?: string;
  birth_date?: string;
  inn?: string;
  tax_id?: string; // Унифицированное поле для ИНН (API Standard 2.3)
  /** СНИЛС (API Contract 2.4) */
  ssn?: string;
}

export interface ComplianceCheckProps {
  title: string;
  description: string;
  endpoint: string;
  parserType: ComplianceModule;
  icon?: string;
}

// === ERROR TYPES ===

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  timestamp: string;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

// === RESPONSE STATUS TYPES ===

export interface LoadingState {
  isLoading: boolean;
  loadingMessage?: string;
  progress?: number;
}

export interface ErrorState {
  hasError: boolean;
  error?: ApiError | string;
  retryCount?: number;
}

/** Справочник возможностей OSINT Tools (GET /v2/osint/capabilities). */
export interface OsintCapabilities {
  inputs: string[];
  by_input: Record<string, string[]>;
  module_categories: Record<string, string>;
  categories: string[];
  stubs: string[];
}
